import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { uses24HourTime } from './i18n';
import { Clock, Users, Calendar, ChevronDown, Plus, AlertTriangle, Camera, X, Download, Info, Shuffle, Building, Sun, Moon, Copy, Check, AlertCircle, Volume2, VolumeX, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { incrementScheduleCounter, getScheduleCount } from './firebase';

// Import 8-bit CSS
import './8bit.css';

import { useStatusBar } from './hooks/useStatusBar';
import { useKeyboardScroll } from './hooks/useKeyboardScroll';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { useRecentLocations } from './hooks/useRecentLocations';
import { isNative } from './utils/platform';
import { saveImage } from './utils/saveImage';
import { lightTap } from './utils/haptics';
import {
  loadSettings,
  saveSettings,
  resetSettings,
  isAtDefaults,
  DEFAULTS as SETTINGS_DEFAULTS,
  VOLUNTEERS_MIN,
  VOLUNTEERS_MAX,
  clampSlotCount,
  EXTRA_VOLUNTEERS_MAX,
} from './utils/scheduleSettings';
import { createSchedule } from './utils/scheduleGeneration';
import AppNudge from './components/AppNudge';
import LocationInput from './components/LocationInput';
import StoreBadge from './components/StoreBadge';
import { i18nEnabled } from './i18n';
import packageInfo from '../package.json';
const appVersion = packageInfo.version;
const LanguageSwitcher = React.lazy(() => import('./components/LanguageSwitcher'));
const BackToBackConfirmDialog = React.lazy(() => import('./components/BackToBackConfirmDialog'));

const ScheduleBuilder = () => {
  const { t, i18n } = useTranslation();
  const [volunteers, setVolunteers] = useState(['', '', '', '', '', '']);
  const [volunteerMap, setVolunteerMap] = useState({});  // Added volunteerMap
  const [locationName, setLocationName] = useState('');
  const { recents: recentLocations, addRecent: addRecentLocation, removeRecent: removeRecentLocation } = useRecentLocations();
  const [timeRange, setTimeRange] = useState({
    startTime: '08:00', 
    endTime: '12:00', 
    interval: 30,
    isCustomInterval: false,
    customInterval: 30,
    date: new Date().toISOString().split('T')[0]
  });
  const [dateEnabled, setDateEnabled] = useState(false); // New state for optional date
  const [schedule, setSchedule] = useState([]);
  const [colors, setColors] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [duplicateError, setDuplicateError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, count: 0 });
  const [darkMode, setDarkMode] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null); // 'copying', 'success', 'error'
  const [schedulesGenerated, setSchedulesGenerated] = useState(0);
  //Gif click states
  const [clickCount, setClickCount] = useState(0);
  const [pokeSequenceCount, setPokeSequenceCount] = useState(0);
  const [showPokeAnimation, setShowPokeAnimation] = useState(false);
  const [showLowHpAnimation, setShowLowHpAnimation] = useState(false);
  const [showFellAnimation, setShowFellAnimation] = useState(false);

  // Advanced schedule settings — hydrated from localStorage
  const initialSettings = loadSettings();
  const [volunteersPerShift, setVolunteersPerShiftState] = useState(initialSettings.volunteersPerShift);
  const [allowBackToBack, setAllowBackToBackState] = useState(initialSettings.allowBackToBack);
  const [advancedExpanded, setAdvancedExpanded] = useState(!isAtDefaults(initialSettings));

  useStatusBar(darkMode);
  useKeyboardScroll();
  const handleAndroidBack = useCallback(() => {
    if (screenshotMode) {
      setScreenshotMode(false);
      return true; // handled → don't exit
    }
    return false; // not handled → exit app
  }, [screenshotMode]);
  useAndroidBackButton(handleAndroidBack);

  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch (err) {
        console.warn('SplashScreen.hide failed:', err);
      }
    })();
  }, []);

  // Updated function with corrected logic
  const handleWaveClick = (event) => {
    // If already showing the final "fell" animation, do nothing
    if (showFellAnimation) return;
    
    // Get the click position and image dimensions
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = rect.width;
    
    // Only count if clicked on the LEFT half of the image
    if (x < width / 2) {
      // Increment the click counter
      const newClickCount = clickCount + 1;
      setClickCount(newClickCount);
      
      // If we've reached 3 clicks
      if (newClickCount >= 3) {
        // Reset click counter
        setClickCount(0);
        
        // Determine which animation to show based on sequence count
        if (showLowHpAnimation) {
          // If we're at low HP and get triggered again, switch to fell animation permanently
          setShowLowHpAnimation(false);
          setShowFellAnimation(true);
          // No timeout - fell.gif remains until page reload
        } 
        else if (pokeSequenceCount >= 2) {
          // After 2 poking sequences, trigger the low HP animation on the 3rd
          setShowLowHpAnimation(true);
          
          // Increment sequence count (this will be 3 now)
          setPokeSequenceCount(pokeSequenceCount + 1);
          
          // Reset to normal after 5 seconds
          setTimeout(() => {
            setShowLowHpAnimation(false);
          }, 2000);
        } 
        else {
          // Regular poke animation for the first 2 sequences
          setShowPokeAnimation(true);
          
          // Increment the sequence counter
          setPokeSequenceCount(pokeSequenceCount + 1);
          
          // Reset after 5 seconds
          setTimeout(() => {
            setShowPokeAnimation(false);
          }, 2000);
        }
      }
    }
  };
  // New states for multiple locations
  const [multipleLocations, setMultipleLocations] = useState(false);
  const [locationNames, setLocationNames] = useState(['Location 1', 'Location 2']);
  
  // SMPW Easter Egg states
  const [titleClickCount, setTitleClickCount] = useState(0);
  const [lastTitleClickTime, setLastTitleClickTime] = useState(0);
  const [smpwMode, setSmpwMode] = useState(false);
  const [titleCollapsed, setTitleCollapsed] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [scripturalPoint, setScripturalPoint] = useState("");
  const [showActivation, setShowActivation] = useState(false);
  
  // New audio Easter egg states
  const [audioMode, setAudioMode] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const audioRef = useRef(null);
  const [activationMessage, setActivationMessage] = useState("");
  
  // SMPW predefined locations
  const smpwLocations = [
    "Eastern Market",
    "Rosa Parks",
    "Campus Martius",
    "Grand Circus Park",
    "Avalon Bakery",
    "Corktown",
    "Wayne State",
    "Airport - Evans Terminal",
    "Airport - McNamara Terminal"
  ];
  
  const scheduleRef = useRef(null);
  
  // Load global schedule count from Firebase
  useEffect(() => {
    try {
      getScheduleCount((count) => {
        setSchedulesGenerated(count);
      });
    } catch (error) {
      console.error("Error fetching schedule count:", error);
      // Fallback to localStorage if Firebase fails
      const localCount = localStorage.getItem('schedulesGenerated');
      if (localCount) {
        setSchedulesGenerated(parseInt(localCount));
      } else {
        setSchedulesGenerated(0);
      }
    }
  }, []);

  // Apply 8-bit mode when audio is activated
  useEffect(() => {
    if (audioMode) {
      // Load the 8-bit font
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap';
      document.head.appendChild(fontLink);
      
      // Add 8-bit class to body
      document.body.classList.add('eight-bit-mode');
      
      // Add scanline effect
      const scanlineEffect = document.createElement('div');
      scanlineEffect.className = 'scanline-effect';
      document.body.appendChild(scanlineEffect);
      
      // Regenerate colors if schedule exists to apply 8-bit palette
      if (schedule.length > 0) {
        const internalVolunteers = Object.keys(volunteerMap);
        setColors(generateColorsForIds(internalVolunteers, true));
      }
      
      return () => {
        // Clean up on unmount or when audioMode changes to false
        document.body.classList.remove('eight-bit-mode');
        const existingScanline = document.querySelector('.scanline-effect');
        if (existingScanline) {
          existingScanline.remove();
        }
      };
    } else {
      // Remove 8-bit class from body
      document.body.classList.remove('eight-bit-mode');
      
      // Remove scanline effect
      const existingScanline = document.querySelector('.scanline-effect');
      if (existingScanline) {
        existingScanline.remove();
      }
      
      // Regenerate colors with normal palette if schedule exists
      if (schedule.length > 0) {
        const internalVolunteers = Object.keys(volunteerMap);
        setColors(generateColorsForIds(internalVolunteers));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioMode]);
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };
  
  // Apply dark mode to document body when it changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Regenerate colors when mode changes if schedule exists
    if (schedule.length > 0) {
      const internalVolunteers = Object.keys(volunteerMap);
      setColors(generateColorsForIds(internalVolunteers));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode, volunteerMap]);

  // Title animation on mount
  useEffect(() => {
    // Skip animation if Easter egg modes are already active
    if (smpwMode || audioMode) {
      setTitleCollapsed(true);
      setShowSubtitle(true);
      return;
    }
    const collapseTimer = setTimeout(() => setTitleCollapsed(true), 1500);
    const subtitleTimer = setTimeout(() => setShowSubtitle(true), 3000);
    return () => {
      clearTimeout(collapseTimer);
      clearTimeout(subtitleTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle date enabled
  const toggleDateEnabled = () => {
    setDateEnabled(prev => !prev);
  };
  
  // Handle title click for Easter egg
  const handleTitleClick = () => {
    const currentTime = new Date().getTime();
    
    // Reset counter if too much time between clicks (1.5 seconds)
    if (currentTime - lastTitleClickTime > 1500 && titleClickCount > 0) {
      setTitleClickCount(0);
    }
    
    const newCount = titleClickCount + 1;
    setTitleClickCount(newCount);
    setLastTitleClickTime(currentTime);
    
    // Activate SMPW mode on 10th click
    if (newCount === 10) {
      setSmpwMode(true);
      setActivationMessage(t('activation.smpw'));
      setShowActivation(true);
      setTimeout(() => setShowActivation(false), 2000);
      // Don't reset counter here so we can count to 15
    }
    
    // Activate audio on 30th click (5 more after SMPW mode)
    if (newCount === 30) {
      setAudioMode(true);
      setActivationMessage(t('activation.audio'));
      setShowActivation(true);
      setTimeout(() => setShowActivation(false), 2000);
      setTitleClickCount(0); // Reset counter after 15 clicks
      
      // Play audio
      if (audioRef.current && !audioMuted) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.error('Audio playback failed:', e));
      }
    }
  };
  
  // Handle volunteer input change
  const handleVolunteerChange = (index, value) => {
    const newVolunteers = [...volunteers];
    newVolunteers[index] = value;
    setVolunteers(newVolunteers);
  };
  
  // Delete volunteer entry
  const deleteVolunteer = (index) => {
    // Prevent deletion if only 2 volunteers remain
    if (volunteers.length <= 2) {
      return;
    }
    
    const newVolunteers = [...volunteers];
    newVolunteers.splice(index, 1);
    setVolunteers(newVolunteers);
  };
  
  // Calculate end time 4 hours after start time
  const calculateEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    let endHours = hours + 4;
    
    // Handle day overflow
    if (endHours >= 24) {
      endHours = endHours - 24;
    }
    
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  // Handle time range changes
  const handleTimeChange = (field, value) => {
    if (field === 'interval') {
      if (value === 'custom') {
        setTimeRange(prev => ({ 
          ...prev, 
          isCustomInterval: true 
        }));
      } else {
        setTimeRange(prev => ({ 
          ...prev, 
          interval: parseInt(value), 
          isCustomInterval: false 
        }));
      }
    } else if (field === 'customInterval') {
      setTimeRange(prev => ({ 
        ...prev, 
        customInterval: parseInt(value),
        interval: parseInt(value)
      }));
    } else if (field === 'startTime') {
      // Auto-calculate end time when start time changes
      const newEndTime = calculateEndTime(value);
      setTimeRange(prev => ({ 
        ...prev, 
        startTime: value,
        endTime: newEndTime 
      }));
    } else {
      setTimeRange(prev => ({ ...prev, [field]: value }));
    }
  };
  
  // Handle location name changes
  const handleLocationNameChange = (index, value) => {
    const newLocationNames = [...locationNames];
    newLocationNames[index] = value;
    setLocationNames(newLocationNames);
  };
  
  // Toggle multiple locations
  const handleMultipleLocationsChange = (e) => {
    const isChecked = e.target.checked;
    setMultipleLocations(isChecked);
    
    // Special behavior for SMPW mode
    if (smpwMode && isChecked) {
      // Set default location names for Eastern Market
      setLocationNames(["Fisher / Russell", "Market/Winder"]);
      
      // Add more volunteer slots
      if (volunteers.length < 11) {
        const additionalSlots = 11 - volunteers.length;
        const newVolunteers = [...volunteers];
        for (let i = 0; i < additionalSlots; i++) {
          newVolunteers.push('');
        }
        setVolunteers(newVolunteers);
      }
    }
  };
  
  // Add a new volunteer field
  const addVolunteer = () => {
    setVolunteers([...volunteers, '']);
  };
  
  // Convert 24h time to 12h format
  const formatShiftTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const mm = minutes.toString().padStart(2, '0');
    if (uses24HourTime(i18n.language)) {
      return `${hours.toString().padStart(2, '0')}:${mm}`;
    }
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${mm}${period}`;
  };
  
  // Format date in a readable way - Fixed to prevent date shifting
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Split the date string into components and create date using local time
    // (this prevents timezone issues that cause the date to shift)
    const [year, month, day] = dateStr.split('-').map(Number);
    // Note: month is 0-indexed in JavaScript Date constructor
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(i18n.language || 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Generate colors for volunteer IDs
  const generateColorsForIds = (volunteerIds, use8BitPalette = false) => {
    const colorMap = {};
    
    // 8-bit color palette (NES inspired)
    const eightBitPalette = [
      { bg: '#D03800', text: '#FCFCFC' }, // Red
      { bg: '#007800', text: '#FCFCFC' }, // Green
      { bg: '#0058F8', text: '#FCFCFC' }, // Blue
      { bg: '#B800B8', text: '#FCFCFC' }, // Purple
      { bg: '#F83800', text: '#FCFCFC' }, // Orange
      { bg: '#008888', text: '#FCFCFC' }, // Teal
      { bg: '#6844FC', text: '#FCFCFC' }, // Indigo
      { bg: '#787878', text: '#FCFCFC' }, // Gray
      { bg: '#F8B800', text: '#000000' }, // Yellow
      { bg: '#00A844', text: '#FCFCFC' }  // Emerald
    ];
    
    // Different color palettes for light and dark mode
    const standardPalette = darkMode ? [
      { bg: '#1a365d', text: '#90cdf4' }, // Dark Blue
      { bg: '#5f370e', text: '#fbd38d' }, // Dark Orange
      { bg: '#1e4620', text: '#9ae6b4' }, // Dark Green
      { bg: '#521b41', text: '#fbb6ce' }, // Dark Pink
      { bg: '#44337a', text: '#d6bcfa' }, // Dark Purple
      { bg: '#154e4e', text: '#81e6d9' }, // Dark Teal
      { bg: '#652b19', text: '#fbd38d' }, // Dark Amber
      { bg: '#2d3748', text: '#e2e8f0' }, // Dark Gray
      { bg: '#22543d', text: '#9ae6b4' }, // Dark Emerald
      { bg: '#702459', text: '#fbb6ce' }  // Dark Rose
    ] : [
      { bg: '#e9f2fc', text: '#1a56db' }, // Blue
      { bg: '#faebd7', text: '#c2410c' }, // Orange
      { bg: '#edf7ed', text: '#15803d' }, // Green
      { bg: '#fce7f3', text: '#be185d' }, // Pink
      { bg: '#f3e8ff', text: '#7e22ce' }, // Purple
      { bg: '#e6fffa', text: '#0d9488' }, // Teal
      { bg: '#fff7ed', text: '#c2410c' }, // Amber
      { bg: '#f1f5f9', text: '#475569' }, // Gray
      { bg: '#f0fdfa', text: '#059669' }, // Emerald
      { bg: '#fdf2f8', text: '#db2777' }  // Rose
    ];
    
    // Choose which palette to use
    const colorPalette = use8BitPalette || audioMode ? eightBitPalette : standardPalette;
    
    volunteerIds.forEach((id, index) => {
      colorMap[id] = colorPalette[index % colorPalette.length];
    });
    
    return colorMap;
  };
  
  // Generate time slots
  const generateTimeSlots = (startTime, endTime, interval) => {
    const slots = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMinute = startMinute;
    
    while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
      const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      
      let nextMinute = currentMinute + interval;
      let nextHour = currentHour;
      
      if (nextMinute >= 60) {
        nextHour += Math.floor(nextMinute / 60);
        nextMinute = nextMinute % 60;
      }
      
      if (nextHour > endHour || (nextHour === endHour && nextMinute > endMinute)) {
        break;
      }
      
      const endTimeString = `${nextHour.toString().padStart(2, '0')}:${nextMinute.toString().padStart(2, '0')}`;
      
      slots.push({
        startTime: timeString,
        endTime: endTimeString,
        display12: `${formatShiftTime(timeString)} - ${formatShiftTime(endTimeString)}`,
        compactDisplay: `${formatShiftTime(timeString)}-${formatShiftTime(endTimeString)}`,
        volunteers: [],
        // For multiple locations, add location-specific volunteer arrays
        locations: multipleLocations ? locationNames.map(name => ({ name, volunteers: [] })) : []
      });
      
      currentHour = nextHour;
      currentMinute = nextMinute;
    }
    
    return slots;
  };
  
  // Shuffle the schedule while maintaining rules
  const shuffleSchedule = () => {
    if (!schedule.length) return;
    lightTap();

    setIsLoading(true);
    
    setTimeout(() => {
      // Use the already established volunteer map
      const internalVolunteers = Object.keys(volunteerMap);

      // Build slots from existing schedule state, projecting per-slot overrides into
      // the shape createSchedule expects:
      //   single-location: slot.volunteerCount (field name already matches)
      //   multi-location:  slot.locationCounts (number[]) derived from slot.locations[i].volunteerCount
      const slotsWithOverrides = schedule.map(s => {
        if (multipleLocations) {
          return {
            ...s,
            locationCounts: (s.locations || []).map(loc => loc.volunteerCount),
          };
        }
        return s; // volunteerCount field name matches generator expectation
      });

      // Create shuffled schedule with randomization flag set to true
      // Generator always tries to avoid back-to-back; the allowBackToBack
      // toggle now only controls the post-generation warning, not generation.
      const { assignments, shiftCounts } = createSchedule(internalVolunteers, slotsWithOverrides, {
        randomize: true,
        multipleLocations,
        locationCount: locationNames.length,
        volunteersPerShift,
        allowBackToBack: false,
      });

      // Create final schedule, restoring override fields onto each new slot
      let newSchedule;

      if (multipleLocations) {
        // For multiple locations — preserve per-location volunteerCount overrides
        newSchedule = schedule.map((origSlot, index) => ({
          ...origSlot,
          volunteers: [], // Keep this for compatibility
          locations: locationNames.map((name, locIndex) => ({
            name,
            volunteers: assignments[index][locIndex] || [],
            volunteerCount: origSlot.locations?.[locIndex]?.volunteerCount,
          })),
          shiftCounts,
        }));
      } else {
        // For single location — volunteerCount is carried via ...origSlot spread
        newSchedule = schedule.map((origSlot, index) => ({
          ...origSlot,
          volunteers: assignments[index],
          locations: [], // Keep this for compatibility
          shiftCounts,
        }));
      }

      setSchedule(newSchedule);
      checkConflicts(newSchedule);
      setIsLoading(false);
    }, 300);
  };
  
  // Check if we should increment the counter (once per hour)
  const shouldIncrementCounter = () => {
    const lastGenTime = localStorage.getItem('lastScheduleGeneration');
    const currentTime = new Date().getTime();
    
    // If no previous generation or it was more than an hour ago
    if (!lastGenTime || (currentTime - parseInt(lastGenTime) > 3600000)) {
      localStorage.setItem('lastScheduleGeneration', currentTime.toString());
      return true;
    }
    
    return false;
  };
  
  // Persisted setters for advanced settings
  const setVolunteersPerShift = (n) => {
    const clamped = Math.max(VOLUNTEERS_MIN, Math.min(VOLUNTEERS_MAX, n));
    setVolunteersPerShiftState(clamped);
    saveSettings({ volunteersPerShift: clamped, allowBackToBack });
  };

  const setAllowBackToBack = (value) => {
    setAllowBackToBackState(value);
    saveSettings({ volunteersPerShift, allowBackToBack: value });
  };

  const resetAdvancedSettings = () => {
    resetSettings();
    setVolunteersPerShiftState(SETTINGS_DEFAULTS.volunteersPerShift);
    setAllowBackToBackState(SETTINGS_DEFAULTS.allowBackToBack);
  };

  // Generate the schedule
  const generateSchedule = () => {
    lightTap();
    const filteredVolunteers = volunteers.filter(v => v.trim() !== '');

    const minVolunteers = multipleLocations
      ? volunteersPerShift * locationNames.length
      : volunteersPerShift;
    if (filteredVolunteers.length < minVolunteers) {
      alert(t('alerts.minVolunteers', { n: minVolunteers }));
      return;
    }

    // Save free-form single-location names to recents (skips SMPW preset list and multi-location mode).
    if (!smpwMode && !multipleLocations) {
      addRecentLocation(locationName);
    }

    setIsLoading(true);
    setDuplicateError(null);
    
    // Simulate loading for better UX
    setTimeout(() => {
      // Generate internal IDs for each volunteer while preserving their display names
      const newVolunteerMap = {};
      const internalVolunteers = filteredVolunteers.map((name, index) => {
        const internalId = `volunteer-${index}`;
        newVolunteerMap[internalId] = name.trim();
        return internalId;
      });
      setVolunteerMap(newVolunteerMap);
      
      // Generate colors - now using internal IDs
      const colorMap = generateColorsForIds(internalVolunteers, audioMode);
      setColors(colorMap);
      
      // Get interval value - either the selected preset or custom value
      const intervalValue = timeRange.isCustomInterval ? 
        timeRange.customInterval : 
        parseInt(timeRange.interval);
      
      // Generate time slots
      const slots = generateTimeSlots(
        timeRange.startTime, 
        timeRange.endTime, 
        intervalValue
      );
      
      // Create schedule with the internal IDs
      // Generator always tries to avoid back-to-back; the allowBackToBack
      // toggle now only controls the post-generation warning, not generation.
      const { assignments, shiftCounts } = createSchedule(internalVolunteers, slots, {
        randomize: false,
        multipleLocations,
        locationCount: locationNames.length,
        volunteersPerShift,
        allowBackToBack: false,
      });
      
      // Create final schedule
      let newSchedule;
      
      if (multipleLocations) {
        // For multiple locations
        newSchedule = slots.map((slot, index) => ({
          ...slot,
          volunteers: [], // Keep this for compatibility
          locations: locationNames.map((name, locIndex) => ({
            name,
            volunteers: assignments[index][locIndex] || []
          })),
          shiftCounts
        }));
      } else {
        // For single location
        newSchedule = slots.map((slot, index) => ({
          ...slot,
          volunteers: assignments[index],
          locations: [], // Keep this for compatibility
          shiftCounts
        }));
      }
      
      // Per-slot volunteer count overrides are intentionally reset on regenerate —
      // slot identity has changed (new time range or interval).
      setSchedule(newSchedule);
      checkConflicts(newSchedule);
      setIsLoading(false);

      // Check if we should increment counter (hourly limit)
      if (shouldIncrementCounter()) {
        // Increment the counter in Firebase with error handling
        try {
          incrementScheduleCounter()
            .then(newCount => {
              if (newCount !== null) {
                setSchedulesGenerated(newCount);
              }
            })
            .catch(error => {
              console.error("Error incrementing counter:", error);
              // Fallback to local storage if Firebase fails
              const newLocalCount = schedulesGenerated + 1;
              setSchedulesGenerated(newLocalCount);
              localStorage.setItem('schedulesGenerated', newLocalCount.toString());
            });
        } catch (error) {
          console.error("Error with counter function:", error);
          // Fallback to local storage if Firebase fails
          const newLocalCount = schedulesGenerated + 1;
          setSchedulesGenerated(newLocalCount);
          localStorage.setItem('schedulesGenerated', newLocalCount.toString());
        }
      }
    }, 500);
  };
  
  // Adjust per-slot volunteer count in single-location preview
  const adjustSlotVolunteerCount = (slotIndex, delta) => {
    setSchedule(prev => {
      const next = [...prev];
      const slot = { ...next[slotIndex] };
      const current = slot.volunteerCount ?? volunteersPerShift;
      const proposed = current + delta;
      const clamped = clampSlotCount(proposed, volunteersPerShift);
      if (clamped === current) return prev; // no-op

      // Adjust the volunteers array in-place: truncate on decrease, pad with null on increase.
      const newVolunteers = [...(slot.volunteers || [])];
      if (clamped < newVolunteers.length) {
        newVolunteers.length = clamped;
      } else {
        while (newVolunteers.length < clamped) newVolunteers.push(null);
      }
      slot.volunteers = newVolunteers;
      slot.volunteerCount = clamped;
      next[slotIndex] = slot;
      return next;
    });
  };

  // Adjust per-(slot, location) volunteer count in multi-location preview
  const adjustLocationVolunteerCount = (slotIndex, locIndex, delta) => {
    setSchedule(prev => {
      const next = [...prev];
      const slot = { ...next[slotIndex] };
      const locations = [...(slot.locations || [])];
      const location = { ...locations[locIndex] };
      const current = location.volunteerCount ?? volunteersPerShift;
      const proposed = current + delta;
      const clamped = clampSlotCount(proposed, volunteersPerShift);
      if (clamped === current) return prev;

      const newVolunteers = [...(location.volunteers || [])];
      if (clamped < newVolunteers.length) {
        newVolunteers.length = clamped;
      } else {
        while (newVolunteers.length < clamped) newVolunteers.push(null);
      }
      location.volunteers = newVolunteers;
      location.volunteerCount = clamped;
      locations[locIndex] = location;
      slot.locations = locations;
      next[slotIndex] = slot;
      return next;
    });
  };

  // Update a volunteer in the schedule
  const updateScheduleVolunteer = (slotIndex, volunteerIndex, newVolunteer, locationIndex = null) => {
    const updatedSchedule = [...schedule];
    
    if (multipleLocations && locationIndex !== null) {
      // For multiple locations
      const currentSlot = updatedSchedule[slotIndex];
      const currentLocation = currentSlot.locations[locationIndex];
      
      // Check if this would create a duplicate in the same shift at this location
      const isDuplicate = newVolunteer && currentLocation.volunteers.some(
        (v, idx) => idx !== volunteerIndex && v === newVolunteer
      );
      if (isDuplicate) {
        setDuplicateError({
          slotIndex,
          locationIndex,
          message: `${volunteerMap[newVolunteer] || newVolunteer} is already assigned to this shift`
        });
        return; // Don't update if it would create a duplicate
      }
      
      // Check if volunteer is already assigned to another location in this slot
      const isAssignedElsewhere = currentSlot.locations.some((loc, idx) => 
        idx !== locationIndex && loc.volunteers.includes(newVolunteer)
      );
      
      if (isAssignedElsewhere) {
        setDuplicateError({
          slotIndex,
          locationIndex,
          message: `${volunteerMap[newVolunteer] || newVolunteer} is already assigned to another location in this time slot`
        });
        return;
      }
      
      // Clear any existing duplicate error
      if (duplicateError && 
          duplicateError.slotIndex === slotIndex && 
          duplicateError.locationIndex === locationIndex) {
        setDuplicateError(null);
      }
      
      // Update the volunteer
      updatedSchedule[slotIndex].locations[locationIndex].volunteers[volunteerIndex] = newVolunteer;
    } else {
      // For single location - original logic
      const currentSlot = updatedSchedule[slotIndex];
      
      // Check if this would create a duplicate in the same shift
      const isDuplicate = newVolunteer && currentSlot.volunteers.some(
        (v, idx) => idx !== volunteerIndex && v === newVolunteer
      );
      if (isDuplicate) {
        setDuplicateError({
          slotIndex,
          message: `${volunteerMap[newVolunteer] || newVolunteer} is already assigned to this shift`
        });
        return; // Don't update if it would create a duplicate
      }
      
      // Clear any existing duplicate error
      if (duplicateError && duplicateError.slotIndex === slotIndex) {
        setDuplicateError(null);
      }
      
      // Update the volunteer
      updatedSchedule[slotIndex].volunteers[volunteerIndex] = newVolunteer;
    }
    
    // Update shift counts
    const shiftCounts = {};
    Object.keys(volunteerMap).forEach(id => {
      shiftCounts[id] = 0;
    });
    
    updatedSchedule.forEach(slot => {
      if (multipleLocations) {
        // Count shifts across all locations
        slot.locations.forEach(location => {
          location.volunteers.forEach(v => {
            if (v && shiftCounts[v] !== undefined) {
              shiftCounts[v]++;
            }
          });
        });
      } else {
        // Original counting logic
        slot.volunteers.forEach(v => {
          if (v && shiftCounts[v] !== undefined) {
            shiftCounts[v]++;
          }
        });
      }
    });
    
    // Update the schedule with new shift counts
    updatedSchedule.forEach(slot => {
      slot.shiftCounts = shiftCounts;
    });
    
    setSchedule(updatedSchedule);
    checkConflicts(updatedSchedule);
  };
  
  // Check for scheduling conflicts
  const checkConflicts = (updatedSchedule) => {
    if (allowBackToBack) {
      setConflicts([]);
      return;
    }
    const newConflicts = [];

    updatedSchedule.forEach((slot, index) => {
      if (index > 0) {
        const previousSlot = updatedSchedule[index - 1];
        
        if (multipleLocations) {
          // For multiple locations
          slot.locations.forEach((location, locIndex) => {
            location.volunteers.forEach(volunteer => {
              // Check if volunteer was in any location in the previous slot
              const wasInPreviousSlot = previousSlot.locations.some(
                prevLoc => prevLoc.volunteers.includes(volunteer)
              );
              
              if (wasInPreviousSlot) {
                newConflicts.push({
                  slotIndex: index,
                  locationIndex: locIndex,
                  volunteer: volunteer
                });
              }
            });
          });
        } else {
          // Original conflict checking
          slot.volunteers.forEach(volunteer => {
            if (previousSlot.volunteers.includes(volunteer)) {
              newConflicts.push({
                slotIndex: index,
                volunteer: volunteer
              });
            }
          });
        }
      }
    });
    
    setConflicts(newConflicts);
  };
  
  // Proceed past confirm checks into screenshot mode.
  const proceedToScreenshotMode = () => {
    lightTap();
    setScreenshotMode(true);
    setCopyStatus(null);
  };

  // Enter screenshot mode (with soft-warn on back-to-back conflicts).
  const enterScreenshotMode = () => {
    if (duplicateError) {
      alert(t('alerts.duplicateFirst'));
      return;
    }
    if (conflicts.length > 0) {
      const affected = new Set(conflicts.map(c => c.volunteer)).size;
      setConfirmDialog({ open: true, count: affected });
      return;
    }
    proceedToScreenshotMode();
  };
  
  // iOS-compatible copy/share function
  // Share schedule image with messaging apps
  const copyScheduleToClipboard = async () => {
    lightTap();
    if (!schedule.length) return;
    
    setCopyStatus('copying');

    try {
      // Create a canvas element with appropriate dimensions
      const canvas = document.createElement('canvas');

      // Per-slot/per-location volunteer count maximums (mirrors Preview UI logic)
      const perNameWidth = multipleLocations ? 130 : 150;
      const maxSingleLocCount = schedule.length === 0
        ? volunteersPerShift
        : Math.max(...schedule.map(s => s.volunteerCount ?? volunteersPerShift));
      const maxLocCount = multipleLocations
        ? locationNames.map((_, locIndex) =>
            schedule.length === 0
              ? volunteersPerShift
              : Math.max(...schedule.map(s =>
                  (s.locations?.[locIndex]?.volunteerCount) ?? volunteersPerShift
                ))
          )
        : null;

      // Configuration for the schedule rendering
      const config = {
        padding: 20,
        headerHeight: 60,
        rowHeight: 30,
        timeColumnWidth: 155,
        volunteerColumnWidth: perNameWidth * (multipleLocations ? volunteersPerShift : maxSingleLocCount), // Per-name width × max volunteer count
        legendHeight: 40,
        cornerRadius: audioMode ? 0 : 2, // No rounded corners in 8-bit mode
        borderColor: darkMode ? '#4a5568' : '#e2e8f0',
        headerBgColor: darkMode ? '#2d3748' : '#f7fafc',
        alternateRowColor: darkMode ? '#2d3748' : '#f7fafc',
        mainRowColor: darkMode ? '#1a202c' : '#ffffff',
        textColor: darkMode ? '#e2e8f0' : '#1a202c',
        mutedTextColor: darkMode ? '#a0aec0' : '#4a5568',
        fontFamily: audioMode ? '"Press Start 2P", cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        pixelRatio: 2 // For higher resolution
      };

      // Calculate canvas dimensions
      const totalVolunteerWidth = multipleLocations
        ? maxLocCount.reduce((sum, n) => sum + perNameWidth * n, 0)
        : perNameWidth * maxSingleLocCount;

      // Compute per-location left edges using actual per-location widths.
      const locXOffsets = [];
      if (multipleLocations) {
        let runningX = config.padding + config.timeColumnWidth;
        for (let i = 0; i < locationNames.length; i++) {
          locXOffsets.push(runningX);
          runningX += perNameWidth * maxLocCount[i];
        }
      }

      const width = config.timeColumnWidth + totalVolunteerWidth + config.padding * 2;
      let height = config.headerHeight + ((schedule.length + 1) * config.rowHeight) + config.legendHeight + config.padding * 2;

      // Add space for discussion point
      let scripturalPointHeight = 0;
      if (scripturalPoint) {
        // Estimate height based on text length (rough estimate)
        const paragraphs = scripturalPoint.split('\n');
        const charsPerLine = audioMode ? 30 : 45; // Fewer chars per line in 8-bit mode
        const lines = paragraphs.reduce((total, p) => {
          if (p.trim() === '') return total + 1; // Blank line
          return total + Math.ceil(p.length / charsPerLine);
        }, 0);
        const lineH = audioMode ? 12 : 20;
        scripturalPointHeight = lines * lineH + 40; // line height + padding
        height += scripturalPointHeight;
      }
      
      // Set canvas size with pixel ratio for high resolution
      canvas.width = width * config.pixelRatio;
      canvas.height = height * config.pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      const ctx = canvas.getContext('2d');
      ctx.scale(config.pixelRatio, config.pixelRatio);
      
      // Apply anti-aliasing - disable for 8-bit mode
      ctx.imageSmoothingEnabled = !audioMode;
      if (!audioMode) {
        ctx.imageSmoothingQuality = 'high';
      }
      
      // Fill background
      ctx.fillStyle = darkMode ? '#1a202c' : '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Add scanlines in 8-bit mode
      if (audioMode) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        for (let i = 0; i < height; i += 2) {
          ctx.fillRect(0, i, width, 1);
        }
      }
      
      // Draw header
      ctx.fillStyle = darkMode ? '#ffffff' : '#1a202c';
      ctx.font = `${audioMode ? '' : 'bold '}${audioMode ? '12' : '16'}px ${config.fontFamily}`;
      ctx.textAlign = 'center';
      const displayTitle = locationName ? t('screenshot.locationTitle', { location: locationName }) : t('screenshot.defaultTitle');
      ctx.fillText(displayTitle, width / 2, config.padding + 20);
      
      // Draw date/time - only if date is enabled
      ctx.font = `${audioMode ? '8' : '12'}px ${config.fontFamily}`;
      ctx.fillStyle = config.mutedTextColor;
      
      let headerText = '';
      if (dateEnabled) {
        const dateStr = formatDate(timeRange.date);
        const timeStr = `${formatShiftTime(timeRange.startTime)}-${formatShiftTime(timeRange.endTime)}`;
        headerText = `${dateStr} • ${timeStr}`;
      } else {
        headerText = `${formatShiftTime(timeRange.startTime)}-${formatShiftTime(timeRange.endTime)}`;
      }
      
      ctx.fillText(headerText, width / 2, config.padding + 40);

      // Draw Discussion Point if present - positioned between header and table
      let discussionPointHeight = 0;
      if (scripturalPoint) {
        const discussionLabel = smpwMode ? t('schedulePanel.discussionScriptural') : t('schedulePanel.discussionPoint');
        const discussionY = config.padding + config.headerHeight;

        // Draw Discussion Point background box
        const boxPadding = 12;
        const boxWidth = width - config.padding * 2;

        // Calculate text wrapping
        ctx.font = `${audioMode ? '8' : '15'}px ${config.fontFamily}`;
        const maxTextWidth = boxWidth - boxPadding * 2;

        // Word wrap function that respects newlines/paragraphs
        const wrapText = (text, maxWidth) => {
          const paragraphs = text.split('\n');
          const lines = [];

          paragraphs.forEach(paragraph => {
            if (paragraph.trim() === '') {
              lines.push(''); // Preserve blank lines
              return;
            }
            const words = paragraph.split(' ');
            let currentLine = '';

            words.forEach(word => {
              const testLine = currentLine ? currentLine + ' ' + word : word;
              const metrics = ctx.measureText(testLine);

              if (metrics.width > maxWidth && currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            });

            if (currentLine) {
              lines.push(currentLine);
            }
          });

          return lines;
        };

        const wrappedLines = wrapText(scripturalPoint, maxTextWidth);
        const lineHeight = audioMode ? 12 : 20;
        const labelHeight = audioMode ? 12 : 16;
        discussionPointHeight = labelHeight + (wrappedLines.length * lineHeight) + boxPadding * 2 + 10; // Extra spacing

        // Draw box background
        ctx.fillStyle = darkMode ? '#2d3748' : '#f0f4ff';
        ctx.fillRect(config.padding, discussionY, boxWidth, discussionPointHeight - 10);

        // Draw box border
        ctx.strokeStyle = darkMode ? '#4a5568' : '#e2e8f0';
        ctx.lineWidth = audioMode ? 2 : 1;
        ctx.strokeRect(config.padding, discussionY, boxWidth, discussionPointHeight - 10);

        // Draw left accent border
        if (!audioMode) {
          const accentColor = darkMode ? '#818cf8' : '#4f46e5';
          ctx.fillStyle = accentColor;
          ctx.fillRect(config.padding, discussionY, 4, discussionPointHeight - 10);
        }

        // Draw label - centered and larger
        ctx.fillStyle = darkMode ? '#a78bfa' : '#6366f1';
        ctx.font = `${audioMode ? '' : 'bold '}${audioMode ? '10' : '14'}px ${config.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.fillText(discussionLabel, width / 2, discussionY + boxPadding + 12);

        // Draw wrapped text - reset to left alignment
        ctx.fillStyle = darkMode ? '#e2e8f0' : '#1a202c';
        ctx.font = `${audioMode ? '8' : '15'}px ${config.fontFamily}`;
        ctx.textAlign = 'left'; // Reset to left alignment for content text

        wrappedLines.forEach((line, index) => {
          ctx.fillText(
            line,
            config.padding + boxPadding,
            discussionY + boxPadding + labelHeight + 16 + (index * lineHeight)
          );
        });
      }

      // Table dimensions and position - adjusted for Discussion Point
      const tableTop = config.padding + config.headerHeight + discussionPointHeight;
      const tableWidth = width - config.padding * 2;
      const tableHeight = (schedule.length + 1) * config.rowHeight; // +1 for header
      
      // Draw main table background
      ctx.fillStyle = darkMode ? '#1a202c' : '#ffffff';
      ctx.fillRect(config.padding, tableTop, tableWidth, tableHeight);
      
      // Draw header row background
      ctx.fillStyle = config.headerBgColor;
      ctx.fillRect(config.padding, tableTop, tableWidth, config.rowHeight);
      
      // Draw row backgrounds (alternating)
      for (let i = 0; i < schedule.length; i++) {
        if (i % 2 === 1) {
          ctx.fillStyle = config.alternateRowColor;
          // Don't cover the borders - make the background slightly smaller
          ctx.fillRect(
            config.padding + 1, 
            tableTop + config.rowHeight + (i * config.rowHeight) + 1, 
            tableWidth - 2, 
            config.rowHeight - 1
          );
        }
      }
      
      // Draw grid lines AFTER backgrounds so they're always visible
      ctx.strokeStyle = config.borderColor;
      ctx.lineWidth = audioMode ? 2 : 1; // Thicker borders in 8-bit mode
      
      // Outer border
      ctx.strokeRect(config.padding, tableTop, tableWidth, tableHeight);
      
      // Vertical dividers for columns - draw AFTER the row backgrounds
      if (multipleLocations) {
        // Divider between time and first location
        ctx.beginPath();
        ctx.moveTo(config.padding + config.timeColumnWidth, tableTop);
        ctx.lineTo(config.padding + config.timeColumnWidth, tableTop + tableHeight);
        ctx.stroke();
        
        // Dividers between locations
        for (let i = 1; i < locationNames.length; i++) {
          const dividerX = locXOffsets[i];
          ctx.beginPath();
          ctx.moveTo(dividerX, tableTop);
          ctx.lineTo(dividerX, tableTop + tableHeight);
          ctx.stroke();
        }
      } else {
        // Original single divider
        ctx.beginPath();
        ctx.moveTo(config.padding + config.timeColumnWidth, tableTop);
        ctx.lineTo(config.padding + config.timeColumnWidth, tableTop + tableHeight);
        ctx.stroke();
      }
      
      // Horizontal grid lines - draw AFTER the row backgrounds
      for (let i = 1; i <= schedule.length + 1; i++) {
        ctx.beginPath();
        ctx.moveTo(config.padding, tableTop + i * config.rowHeight);
        ctx.lineTo(width - config.padding, tableTop + i * config.rowHeight);
        ctx.stroke();
      }
      
      // Draw header text
      ctx.fillStyle = config.textColor;
      ctx.font = `${audioMode ? '8' : '500 12'}px ${config.fontFamily}`;
      ctx.textAlign = 'left';
      ctx.fillText(t('schedulePanel.timeHeader'), config.padding + 10, tableTop + config.rowHeight / 2 + 5);
      
      if (multipleLocations) {
        // Location headers
        locationNames.forEach((name, locIndex) => {
          const locX = locXOffsets[locIndex];
          ctx.textAlign = 'center';
          
          // Handle 8-bit mode - truncate/resize text if needed
          let displayName = name;
          if (audioMode) {
            // Measure text width
            const maxWidth = perNameWidth * maxLocCount[locIndex] - 10; // Allow some padding
            const textWidth = ctx.measureText(name).width;
            
            // Truncate if too long
            if (textWidth > maxWidth) {
              // Try to fit with ellipsis
              let truncated = name;
              while (ctx.measureText(truncated + '...').width > maxWidth && truncated.length > 0) {
                truncated = truncated.slice(0, -1);
              }
              displayName = truncated + (truncated.length < name.length ? '...' : '');
            }
          }
          
          ctx.fillText(displayName, locX + (perNameWidth * maxLocCount[locIndex]) / 2, tableTop + config.rowHeight / 2 + 5);
        });
      } else {
        // Original volunteers header
        ctx.textAlign = 'center';
        ctx.fillText(t('schedulePanel.volunteersHeader'), config.padding + config.timeColumnWidth + config.volunteerColumnWidth / 2,
                  tableTop + config.rowHeight / 2 + 5);
      }
      
      // Draw schedule rows (data rows)
      schedule.forEach((slot, index) => {
        // Calculate row position (offset by header row)
        const y = tableTop + config.rowHeight + (index * config.rowHeight);
        
        // Time text
ctx.fillStyle = config.textColor;
// Always use the smaller font size for 8-bit mode time column for consistency
ctx.font = audioMode ? `6px ${config.fontFamily}` : `500 12px ${config.fontFamily}`;
ctx.textAlign = 'left';
ctx.fillText(slot.compactDisplay, config.padding + 10, y + config.rowHeight / 2 + 5);
        
        if (multipleLocations) {
          // Draw volunteer boxes for each location
          slot.locations.forEach((location, locIndex) => {
            const locX = locXOffsets[locIndex];
            
            // Draw volunteer boxes
            const locCount = (slot.locations?.[locIndex]?.volunteerCount) ?? volunteersPerShift;
            const volunteerWidth = (perNameWidth * maxLocCount[locIndex]) / locCount - 8;
            location.volunteers.forEach((volunteerId, vIndex) => {
              if (!volunteerId) return;
              
              const displayName = volunteerMap[volunteerId] || volunteerId;
              const vx = locX + 4 + vIndex * (volunteerWidth + 8);
              const vy = y + 5;
              const vHeight = config.rowHeight - 10;
              
              // Volunteer background - draw a rounded rectangle or square in 8-bit mode
              const radius = config.cornerRadius;
              ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
              
              if (audioMode) {
                // Square box for 8-bit mode
                ctx.fillRect(vx, vy, volunteerWidth, vHeight);
                // Draw border
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 2;
                ctx.strokeRect(vx, vy, volunteerWidth, vHeight);
              } else {
                // Draw rounded rectangle
                ctx.beginPath();
                ctx.moveTo(vx + radius, vy);
                ctx.lineTo(vx + volunteerWidth - radius, vy);
                ctx.quadraticCurveTo(vx + volunteerWidth, vy, vx + volunteerWidth, vy + radius);
                ctx.lineTo(vx + volunteerWidth, vy + vHeight - radius);
                ctx.quadraticCurveTo(vx + volunteerWidth, vy + vHeight, vx + volunteerWidth - radius, vy + vHeight);
                ctx.lineTo(vx + radius, vy + vHeight);
                ctx.quadraticCurveTo(vx, vy + vHeight, vx, vy + vHeight - radius);
                ctx.lineTo(vx, vy + radius);
                ctx.quadraticCurveTo(vx, vy, vx + radius, vy);
                ctx.closePath();
                ctx.fill();
              }
              
              // Volunteer text with truncation for 8-bit mode
              ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
              
              // Handle text sizing in 8-bit mode
              if (audioMode) {
                const maxVolWidth = volunteerWidth - 6; // Allow some padding
                ctx.font = `8px ${config.fontFamily}`;
                let truncated = displayName;
                
                // Measure and truncate if needed
                if (ctx.measureText(displayName).width > maxVolWidth) {
                  while (ctx.measureText(truncated + '...').width > maxVolWidth && truncated.length > 0) {
                    truncated = truncated.slice(0, -1);
                  }
                  truncated = truncated + (truncated.length < displayName.length ? '...' : '');
                }
                
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(truncated, vx + volunteerWidth / 2, vy + vHeight / 2);
              } else {
                // Normal mode
                ctx.font = `11px ${config.fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(displayName, vx + volunteerWidth / 2, vy + vHeight / 2);
              }
              
              ctx.textBaseline = 'alphabetic';
            });
          });
        } else {
          // Original volunteer rendering for single location
          const rowCount = slot.volunteerCount ?? volunteersPerShift;
          const volunteerWidth = (perNameWidth * maxSingleLocCount) / rowCount - 8;
          slot.volunteers.forEach((volunteerId, vIndex) => {
            if (!volunteerId) return;
            
            const displayName = volunteerMap[volunteerId] || volunteerId;
            const vx = config.padding + config.timeColumnWidth + 4 + vIndex * (volunteerWidth + 8);
            const vy = y + 5;
            const vHeight = config.rowHeight - 10;
            
            // Volunteer background
            const radius = config.cornerRadius;
            ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
            
            if (audioMode) {
              // Square box for 8-bit mode
              ctx.fillRect(vx, vy, volunteerWidth, vHeight);
              // Draw border
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 2;
              ctx.strokeRect(vx, vy, volunteerWidth, vHeight);
            } else {
              // Draw rounded rectangle
              ctx.beginPath();
              ctx.moveTo(vx + radius, vy);
              ctx.lineTo(vx + volunteerWidth - radius, vy);
              ctx.quadraticCurveTo(vx + volunteerWidth, vy, vx + volunteerWidth, vy + radius);
              ctx.lineTo(vx + volunteerWidth, vy + vHeight - radius);
              ctx.quadraticCurveTo(vx + volunteerWidth, vy + vHeight, vx + volunteerWidth - radius, vy + vHeight);
              ctx.lineTo(vx + radius, vy + vHeight);
              ctx.quadraticCurveTo(vx, vy + vHeight, vx, vy + vHeight - radius);
              ctx.lineTo(vx, vy + radius);
              ctx.quadraticCurveTo(vx, vy, vx + radius, vy);
              ctx.closePath();
              ctx.fill();
            }
            
            // Volunteer text
            ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
            
            // Handle text sizing in 8-bit mode
            if (audioMode) {
              const maxVolWidth = volunteerWidth - 6; // Allow some padding
              ctx.font = `8px ${config.fontFamily}`;
              let truncated = displayName;
              
              // Measure and truncate if needed
              if (ctx.measureText(displayName).width > maxVolWidth) {
                while (ctx.measureText(truncated + '...').width > maxVolWidth && truncated.length > 0) {
                  truncated = truncated.slice(0, -1);
                }
                truncated = truncated + (truncated.length < displayName.length ? '...' : '');
              }
              
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(truncated, vx + volunteerWidth / 2, vy + vHeight / 2);
            } else {
              // Normal mode
              ctx.font = `11px ${config.fontFamily}`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(displayName, vx + volunteerWidth / 2, vy + vHeight / 2);
            }
            
            ctx.textBaseline = 'alphabetic';
          });
        }
      });
      
      // Draw legend
      const legendY = tableTop + tableHeight + 10;
      const shiftCounts = schedule[0]?.shiftCounts || {};
      const volunteers = Object.keys(shiftCounts);
      
      let legendX = config.padding;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${audioMode ? '8' : '11'}px ${config.fontFamily}`;
      
      volunteers.forEach(volunteerId => {
        const count = shiftCounts[volunteerId];
        const displayName = volunteerMap[volunteerId] || volunteerId;
        
        // Handle truncation for 8-bit mode
        let legendText;
        if (audioMode) {
          // Keep shorter in 8-bit mode
          const maxNameLength = 8; // arbitrary limit for legend
          let truncName = displayName;
          if (truncName.length > maxNameLength) {
            truncName = truncName.substring(0, maxNameLength - 1) + '…';
          }
          legendText = `${truncName}:${count}`;
        } else {
          legendText = `${displayName}:${count}`;
        }
        
        const textWidth = ctx.measureText(legendText).width;
        const legendWidth = textWidth + 16;
        const legendHeight = 18;
        const radius = config.cornerRadius;
        
        // Background
        ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
        
        if (audioMode) {
          // Square box for 8-bit mode
          ctx.fillRect(legendX, legendY, legendWidth, legendHeight);
          // Draw border
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.strokeRect(legendX, legendY, legendWidth, legendHeight);
        } else {
          // Draw rounded rectangle
          ctx.beginPath();
          ctx.moveTo(legendX + radius, legendY);
          ctx.lineTo(legendX + legendWidth - radius, legendY);
          ctx.quadraticCurveTo(legendX + legendWidth, legendY, legendX + legendWidth, legendY + radius);
          ctx.lineTo(legendX + legendWidth, legendY + legendHeight - radius);
          ctx.quadraticCurveTo(legendX + legendWidth, legendY + legendHeight, legendX + legendWidth - radius, legendY + legendHeight);
          ctx.lineTo(legendX + radius, legendY + legendHeight);
          ctx.quadraticCurveTo(legendX, legendY + legendHeight, legendX, legendY + legendHeight - radius);
          ctx.lineTo(legendX, legendY + radius);
          ctx.quadraticCurveTo(legendX, legendY, legendX + radius, legendY);
          ctx.closePath();
          ctx.fill();
        }
        
        // Text
        ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
        ctx.fillText(legendText, legendX + legendWidth / 2, legendY + legendHeight / 2);
        
        legendX += legendWidth + 6;
      });

      // Convert canvas to blob for sharing
      const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const imageUrl = canvas.toDataURL('image/png');
      
      // Create file name
      const dateString = dateEnabled ? timeRange.date.replace(/-/g, '') : 'nodate';
      const locationText = locationName ? `${locationName.replace(/\s+/g, '_')}` : 'Volunteer';
      const filename = `${locationText}_Schedule_${dateString}.png`;
      
      // Detect mobile vs desktop — native share sheet is only useful on mobile
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isMobile = isIOS || /Android/i.test(navigator.userAgent) || 'ontouchstart' in window;

      // Native (Capacitor): skip Web Share API entirely and use saveImage,
      // which writes the PNG to the cache and opens the native share sheet.
      if (isNative()) {
        try {
          const result = await saveImage({ dataUrl: imageUrl, filename });
          if (result && result.cancelled) {
            setCopyStatus(null);
          } else {
            setCopyStatus('success');
            setTimeout(() => setCopyStatus(null), 2000);
          }
        } catch (err) {
          console.error('Native share error:', err);
          setCopyStatus('error');
        }
        return;
      }

      // Use Web Share API only on mobile where the share sheet has real options
      if (isMobile && navigator.share && navigator.canShare) {
        try {
          const file = new File([imageBlob], filename, { type: 'image/png' });
          const shareData = {
            files: [file],
            title: t('screenshot.share.apiTitle')
          };

          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            setCopyStatus('success');
            setTimeout(() => setCopyStatus(null), 2000);
            return;
          }
        } catch (shareError) {
          if (shareError.name === 'AbortError') {
            setCopyStatus(null);
            return;
          }
          console.log('Share API file sharing error:', shareError);
          // Fall through to alternatives
        }
      }

      // Desktop and non-iOS fallback — copy to clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        try {
          const item = new ClipboardItem({ 'image/png': imageBlob });
          await navigator.clipboard.write([item]);
          setCopyStatus('copied');
          setTimeout(() => setCopyStatus(null), 3000);
          return;
        } catch (clipError) {
          console.log('Clipboard API error:', clipError);
          // Fall through to alternatives
        }
      }
      
      // Last resort (web desktop): use saveImage which falls back to <a download>
      try {
        await saveImage({ dataUrl: imageUrl, filename });
        setCopyStatus('success');
        setTimeout(() => setCopyStatus(null), 2000);
      } catch (downloadError) {
        console.log('Download error:', downloadError);
        setCopyStatus('error');
      }
      
    } catch (err) {
      console.error('Error creating image:', err);
      setCopyStatus('error');
      // copy fallback modal removed
    }
  };
  
  // Download schedule as image - CANVAS BASED APPROACH
  const downloadScheduleImage = async () => {
    if (!schedule.length) return;
    lightTap();

    // Show loading indicator
    const downloadBtn = document.querySelector('.download-btn');
    let originalContent = '';
    if (downloadBtn) {
      originalContent = downloadBtn.innerHTML;
      downloadBtn.innerHTML = `<span>${t('screenshot.downloading')}</span>`;
    }
    
    try {
      // Create a canvas element with appropriate dimensions
      const canvas = document.createElement('canvas');

      // Per-slot/per-location volunteer count maximums (mirrors Preview UI logic)
      const perNameWidth = multipleLocations ? 130 : 150;
      const maxSingleLocCount = schedule.length === 0
        ? volunteersPerShift
        : Math.max(...schedule.map(s => s.volunteerCount ?? volunteersPerShift));
      const maxLocCount = multipleLocations
        ? locationNames.map((_, locIndex) =>
            schedule.length === 0
              ? volunteersPerShift
              : Math.max(...schedule.map(s =>
                  (s.locations?.[locIndex]?.volunteerCount) ?? volunteersPerShift
                ))
          )
        : null;

      // Configuration for the schedule rendering
      const config = {
        padding: 20,
        headerHeight: 60,
        rowHeight: 30,
        timeColumnWidth: 155,
        volunteerColumnWidth: perNameWidth * (multipleLocations ? volunteersPerShift : maxSingleLocCount), // Per-name width × max volunteer count
        legendHeight: 40,
        cornerRadius: audioMode ? 0 : 2, // No rounded corners in 8-bit mode
        borderColor: darkMode ? '#4a5568' : '#e2e8f0',
        headerBgColor: darkMode ? '#2d3748' : '#f7fafc',
        alternateRowColor: darkMode ? '#2d3748' : '#f7fafc',
        mainRowColor: darkMode ? '#1a202c' : '#ffffff',
        textColor: darkMode ? '#e2e8f0' : '#1a202c',
        mutedTextColor: darkMode ? '#a0aec0' : '#4a5568',
        fontFamily: audioMode ? '"Press Start 2P", cursive' : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        pixelRatio: 2 // For higher resolution
      };
      
      // Calculate canvas dimensions
      const totalVolunteerWidth = multipleLocations
        ? maxLocCount.reduce((sum, n) => sum + perNameWidth * n, 0)
        : perNameWidth * maxSingleLocCount;

      // Compute per-location left edges using actual per-location widths.
      const locXOffsets = [];
      if (multipleLocations) {
        let runningX = config.padding + config.timeColumnWidth;
        for (let i = 0; i < locationNames.length; i++) {
          locXOffsets.push(runningX);
          runningX += perNameWidth * maxLocCount[i];
        }
      }

      const width = config.timeColumnWidth + totalVolunteerWidth + config.padding * 2;
      let height = config.headerHeight + ((schedule.length + 1) * config.rowHeight) + config.legendHeight + config.padding * 2;
      
      // Add space for discussion point
      let scripturalPointHeight = 0;
      if (scripturalPoint) {
        // Estimate height based on text length (rough estimate)
        const paragraphs = scripturalPoint.split('\n');
        const charsPerLine = audioMode ? 30 : 45; // Fewer chars per line in 8-bit mode
        const lines = paragraphs.reduce((total, p) => {
          if (p.trim() === '') return total + 1; // Blank line
          return total + Math.ceil(p.length / charsPerLine);
        }, 0);
        const lineH = audioMode ? 12 : 20;
        scripturalPointHeight = lines * lineH + 40; // line height + padding
        height += scripturalPointHeight;
      }
      
      // Set canvas size with pixel ratio for high resolution
      canvas.width = width * config.pixelRatio;
      canvas.height = height * config.pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      canvas.style.height = `${height}px`;
      
      const ctx = canvas.getContext('2d');
      ctx.scale(config.pixelRatio, config.pixelRatio);
      
      // Apply anti-aliasing - disable for 8-bit mode
      ctx.imageSmoothingEnabled = !audioMode;
      if (!audioMode) {
        ctx.imageSmoothingQuality = 'high';
      }
      
      // Fill background
      ctx.fillStyle = darkMode ? '#1a202c' : '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Add scanlines in 8-bit mode
      if (audioMode) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        for (let i = 0; i < height; i += 2) {
          ctx.fillRect(0, i, width, 1);
        }
      }
      
      // Draw header
      ctx.fillStyle = darkMode ? '#ffffff' : '#1a202c';
      ctx.font = `${audioMode ? '' : 'bold '}${audioMode ? '12' : '16'}px ${config.fontFamily}`;
      ctx.textAlign = 'center';
      const displayTitle = locationName ? t('screenshot.locationTitle', { location: locationName }) : t('screenshot.defaultTitle');
      ctx.fillText(displayTitle, width / 2, config.padding + 20);
      
      // Draw date/time - only if date is enabled
      ctx.font = `${audioMode ? '8' : '12'}px ${config.fontFamily}`;
      ctx.fillStyle = config.mutedTextColor;
      
      let headerText = '';
      if (dateEnabled) {
        const dateStr = formatDate(timeRange.date);
        const timeStr = `${formatShiftTime(timeRange.startTime)}-${formatShiftTime(timeRange.endTime)}`;
        headerText = `${dateStr} • ${timeStr}`;
      } else {
        headerText = `${formatShiftTime(timeRange.startTime)}-${formatShiftTime(timeRange.endTime)}`;
      }
      
      ctx.fillText(headerText, width / 2, config.padding + 40);

      // Draw Discussion Point if present - positioned between header and table
      let discussionPointHeight = 0;
      if (scripturalPoint) {
        const discussionLabel = smpwMode ? t('schedulePanel.discussionScriptural') : t('schedulePanel.discussionPoint');
        const discussionY = config.padding + config.headerHeight;

        // Draw Discussion Point background box
        const boxPadding = 12;
        const boxWidth = width - config.padding * 2;

        // Calculate text wrapping
        ctx.font = `${audioMode ? '8' : '15'}px ${config.fontFamily}`;
        const maxTextWidth = boxWidth - boxPadding * 2;

        // Word wrap function that respects newlines/paragraphs
        const wrapText = (text, maxWidth) => {
          const paragraphs = text.split('\n');
          const lines = [];

          paragraphs.forEach(paragraph => {
            if (paragraph.trim() === '') {
              lines.push(''); // Preserve blank lines
              return;
            }
            const words = paragraph.split(' ');
            let currentLine = '';

            words.forEach(word => {
              const testLine = currentLine ? currentLine + ' ' + word : word;
              const metrics = ctx.measureText(testLine);

              if (metrics.width > maxWidth && currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            });

            if (currentLine) {
              lines.push(currentLine);
            }
          });

          return lines;
        };

        const wrappedLines = wrapText(scripturalPoint, maxTextWidth);
        const lineHeight = audioMode ? 12 : 20;
        const labelHeight = audioMode ? 12 : 16;
        discussionPointHeight = labelHeight + (wrappedLines.length * lineHeight) + boxPadding * 2 + 10; // Extra spacing

        // Draw box background
        ctx.fillStyle = darkMode ? '#2d3748' : '#f0f4ff';
        ctx.fillRect(config.padding, discussionY, boxWidth, discussionPointHeight - 10);

        // Draw box border
        ctx.strokeStyle = darkMode ? '#4a5568' : '#e2e8f0';
        ctx.lineWidth = audioMode ? 2 : 1;
        ctx.strokeRect(config.padding, discussionY, boxWidth, discussionPointHeight - 10);

        // Draw left accent border
        if (!audioMode) {
          const accentColor = darkMode ? '#818cf8' : '#4f46e5';
          ctx.fillStyle = accentColor;
          ctx.fillRect(config.padding, discussionY, 4, discussionPointHeight - 10);
        }

        // Draw label - centered and larger
        ctx.fillStyle = darkMode ? '#a78bfa' : '#6366f1';
        ctx.font = `${audioMode ? '' : 'bold '}${audioMode ? '10' : '14'}px ${config.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.fillText(discussionLabel, width / 2, discussionY + boxPadding + 12);

        // Draw wrapped text - reset to left alignment
        ctx.fillStyle = darkMode ? '#e2e8f0' : '#1a202c';
        ctx.font = `${audioMode ? '8' : '15'}px ${config.fontFamily}`;
        ctx.textAlign = 'left'; // Reset to left alignment for content text

        wrappedLines.forEach((line, index) => {
          ctx.fillText(
            line,
            config.padding + boxPadding,
            discussionY + boxPadding + labelHeight + 16 + (index * lineHeight)
          );
        });
      }

      // Table dimensions and position - adjusted for Discussion Point
      const tableTop = config.padding + config.headerHeight + discussionPointHeight;
      const tableWidth = width - config.padding * 2;
      const tableHeight = (schedule.length + 1) * config.rowHeight; // +1 for header
      
      // Draw main table background
      ctx.fillStyle = darkMode ? '#1a202c' : '#ffffff';
      ctx.fillRect(config.padding, tableTop, tableWidth, tableHeight);
      
      // Draw header row background
      ctx.fillStyle = config.headerBgColor;
      ctx.fillRect(config.padding, tableTop, tableWidth, config.rowHeight);
      
      // Draw row backgrounds (alternating)
      for (let i = 0; i < schedule.length; i++) {
        if (i % 2 === 1) {
          ctx.fillStyle = config.alternateRowColor;
          // Don't cover the borders - make the background slightly smaller
          ctx.fillRect(
            config.padding + 1, 
            tableTop + config.rowHeight + (i * config.rowHeight) + 1, 
            tableWidth - 2, 
            config.rowHeight - 1
          );
        }
      }
      
      // Draw grid lines AFTER backgrounds so they're always visible
      ctx.strokeStyle = config.borderColor;
      ctx.lineWidth = audioMode ? 2 : 1; // Thicker borders in 8-bit mode
      
      // Outer border
      ctx.strokeRect(config.padding, tableTop, tableWidth, tableHeight);
      
      // Vertical dividers for columns - draw AFTER the row backgrounds
      if (multipleLocations) {
        // Divider between time and first location
        ctx.beginPath();
        ctx.moveTo(config.padding + config.timeColumnWidth, tableTop);
        ctx.lineTo(config.padding + config.timeColumnWidth, tableTop + tableHeight);
        ctx.stroke();
        
        // Dividers between locations
        for (let i = 1; i < locationNames.length; i++) {
          const dividerX = locXOffsets[i];
          ctx.beginPath();
          ctx.moveTo(dividerX, tableTop);
          ctx.lineTo(dividerX, tableTop + tableHeight);
          ctx.stroke();
        }
      } else {
        // Original single divider
        ctx.beginPath();
        ctx.moveTo(config.padding + config.timeColumnWidth, tableTop);
        ctx.lineTo(config.padding + config.timeColumnWidth, tableTop + tableHeight);
        ctx.stroke();
      }
      
      // Horizontal grid lines - draw AFTER the row backgrounds
      for (let i = 1; i <= schedule.length + 1; i++) {
        ctx.beginPath();
        ctx.moveTo(config.padding, tableTop + i * config.rowHeight);
        ctx.lineTo(width - config.padding, tableTop + i * config.rowHeight);
        ctx.stroke();
      }
      
      // Draw header text
      ctx.fillStyle = config.textColor;
      ctx.font = `${audioMode ? '8' : '500 12'}px ${config.fontFamily}`;
      ctx.textAlign = 'left';
      ctx.fillText(t('schedulePanel.timeHeader'), config.padding + 10, tableTop + config.rowHeight / 2 + 5);
      
      if (multipleLocations) {
        // Location headers
        locationNames.forEach((name, locIndex) => {
          const locX = locXOffsets[locIndex];
          ctx.textAlign = 'center';

          // Handle 8-bit mode - truncate/resize text if needed
          let displayName = name;
          if (audioMode) {
            // Measure text width
            const maxWidth = perNameWidth * maxLocCount[locIndex] - 10; // Allow some padding
            const textWidth = ctx.measureText(name).width;

            // Truncate if too long
            if (textWidth > maxWidth) {
              // Try to fit with ellipsis
              let truncated = name;
              while (ctx.measureText(truncated + '...').width > maxWidth && truncated.length > 0) {
                truncated = truncated.slice(0, -1);
              }
              displayName = truncated + (truncated.length < name.length ? '...' : '');
            }
          }

          ctx.fillText(displayName, locX + (perNameWidth * maxLocCount[locIndex]) / 2, tableTop + config.rowHeight / 2 + 5);
        });
      } else {
        // Original volunteers header
        ctx.textAlign = 'center';
        ctx.fillText(t('schedulePanel.volunteersHeader'), config.padding + config.timeColumnWidth + config.volunteerColumnWidth / 2,
                  tableTop + config.rowHeight / 2 + 5);
      }
      
      // Draw schedule rows (data rows)
      schedule.forEach((slot, index) => {
        // Calculate row position (offset by header row)
        const y = tableTop + config.rowHeight + (index * config.rowHeight);
        
        // Time text
ctx.fillStyle = config.textColor;
// Always use the smaller font size for 8-bit mode time column for consistency
ctx.font = audioMode ? `6px ${config.fontFamily}` : `500 12px ${config.fontFamily}`;
ctx.textAlign = 'left';
ctx.fillText(slot.compactDisplay, config.padding + 10, y + config.rowHeight / 2 + 5);
        
        if (multipleLocations) {
          // Draw volunteer boxes for each location
          slot.locations.forEach((location, locIndex) => {
            const locX = locXOffsets[locIndex];

            // Draw volunteer boxes
            const locCount = (slot.locations?.[locIndex]?.volunteerCount) ?? volunteersPerShift;
            const volunteerWidth = (perNameWidth * maxLocCount[locIndex]) / locCount - 8;
            location.volunteers.forEach((volunteerId, vIndex) => {
              if (!volunteerId) return;
              
              const displayName = volunteerMap[volunteerId] || volunteerId;
              const vx = locX + 4 + vIndex * (volunteerWidth + 8);
              const vy = y + 5;
              const vHeight = config.rowHeight - 10;
              
              // Volunteer background - draw a rounded rectangle or square in 8-bit mode
              const radius = config.cornerRadius;
              ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
              
              if (audioMode) {
                // Square box for 8-bit mode
                ctx.fillRect(vx, vy, volunteerWidth, vHeight);
                // Draw border
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 2;
                ctx.strokeRect(vx, vy, volunteerWidth, vHeight);
              } else {
                // Draw rounded rectangle
                ctx.beginPath();
                ctx.moveTo(vx + radius, vy);
                ctx.lineTo(vx + volunteerWidth - radius, vy);
                ctx.quadraticCurveTo(vx + volunteerWidth, vy, vx + volunteerWidth, vy + radius);
                ctx.lineTo(vx + volunteerWidth, vy + vHeight - radius);
                ctx.quadraticCurveTo(vx + volunteerWidth, vy + vHeight, vx + volunteerWidth - radius, vy + vHeight);
                ctx.lineTo(vx + radius, vy + vHeight);
                ctx.quadraticCurveTo(vx, vy + vHeight, vx, vy + vHeight - radius);
                ctx.lineTo(vx, vy + radius);
                ctx.quadraticCurveTo(vx, vy, vx + radius, vy);
                ctx.closePath();
                ctx.fill();
              }
              
              // Volunteer text with truncation for 8-bit mode
              ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
              
              // Handle text sizing in 8-bit mode
              if (audioMode) {
                const maxVolWidth = volunteerWidth - 6; // Allow some padding
                ctx.font = `8px ${config.fontFamily}`;
                let truncated = displayName;
                
                // Measure and truncate if needed
                if (ctx.measureText(displayName).width > maxVolWidth) {
                  while (ctx.measureText(truncated + '...').width > maxVolWidth && truncated.length > 0) {
                    truncated = truncated.slice(0, -1);
                  }
                  truncated = truncated + (truncated.length < displayName.length ? '...' : '');
                }
                
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(truncated, vx + volunteerWidth / 2, vy + vHeight / 2);
              } else {
                // Normal mode
                ctx.font = `11px ${config.fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(displayName, vx + volunteerWidth / 2, vy + vHeight / 2);
              }
              
              ctx.textBaseline = 'alphabetic';
            });
          });
        } else {
          // Original volunteer rendering for single location
          const rowCount = slot.volunteerCount ?? volunteersPerShift;
          const volunteerWidth = (perNameWidth * maxSingleLocCount) / rowCount - 8;
          slot.volunteers.forEach((volunteerId, vIndex) => {
            if (!volunteerId) return;

            const displayName = volunteerMap[volunteerId] || volunteerId;
            const vx = config.padding + config.timeColumnWidth + 4 + vIndex * (volunteerWidth + 8);
            const vy = y + 5;
            const vHeight = config.rowHeight - 10;
            
            // Volunteer background
            const radius = config.cornerRadius;
            ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
            
            if (audioMode) {
              // Square box for 8-bit mode
              ctx.fillRect(vx, vy, volunteerWidth, vHeight);
              // Draw border
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 2;
              ctx.strokeRect(vx, vy, volunteerWidth, vHeight);
            } else {
              // Draw rounded rectangle
              ctx.beginPath();
              ctx.moveTo(vx + radius, vy);
              ctx.lineTo(vx + volunteerWidth - radius, vy);
              ctx.quadraticCurveTo(vx + volunteerWidth, vy, vx + volunteerWidth, vy + radius);
              ctx.lineTo(vx + volunteerWidth, vy + vHeight - radius);
              ctx.quadraticCurveTo(vx + volunteerWidth, vy + vHeight, vx + volunteerWidth - radius, vy + vHeight);
              ctx.lineTo(vx + radius, vy + vHeight);
              ctx.quadraticCurveTo(vx, vy + vHeight, vx, vy + vHeight - radius);
              ctx.lineTo(vx, vy + radius);
              ctx.quadraticCurveTo(vx, vy, vx + radius, vy);
              ctx.closePath();
              ctx.fill();
            }
            
            // Volunteer text
            ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
            
            // Handle text sizing in 8-bit mode
            if (audioMode) {
              const maxVolWidth = volunteerWidth - 6; // Allow some padding
              ctx.font = `8px ${config.fontFamily}`;
              let truncated = displayName;
              
              // Measure and truncate if needed
              if (ctx.measureText(displayName).width > maxVolWidth) {
                while (ctx.measureText(truncated + '...').width > maxVolWidth && truncated.length > 0) {
                  truncated = truncated.slice(0, -1);
                }
                truncated = truncated + (truncated.length < displayName.length ? '...' : '');
              }ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(truncated, vx + volunteerWidth / 2, vy + vHeight / 2);
            } else {
              // Normal mode
              ctx.font = `11px ${config.fontFamily}`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(displayName, vx + volunteerWidth / 2, vy + vHeight / 2);
            }
            
            ctx.textBaseline = 'alphabetic';
          });
        }
      });
      
      // Draw legend
      const legendY = tableTop + tableHeight + 10;
      const shiftCounts = schedule[0]?.shiftCounts || {};
      const volunteers = Object.keys(shiftCounts);
      
      let legendX = config.padding;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${audioMode ? '8' : '11'}px ${config.fontFamily}`;
      
      volunteers.forEach(volunteerId => {
        const count = shiftCounts[volunteerId];
        const displayName = volunteerMap[volunteerId] || volunteerId;
        
        // Handle truncation for 8-bit mode
        let legendText;
        if (audioMode) {
          // Keep shorter in 8-bit mode
          const maxNameLength = 8; // arbitrary limit for legend
          let truncName = displayName;
          if (truncName.length > maxNameLength) {
            truncName = truncName.substring(0, maxNameLength - 1) + '…';
          }
          legendText = `${truncName}:${count}`;
        } else {
          legendText = `${displayName}:${count}`;
        }
        
        const textWidth = ctx.measureText(legendText).width;
        const legendWidth = textWidth + 16;
        const legendHeight = 18;
        const radius = config.cornerRadius;
        
        // Background
        ctx.fillStyle = colors[volunteerId]?.bg || config.alternateRowColor;
        
        if (audioMode) {
          // Square box for 8-bit mode
          ctx.fillRect(legendX, legendY, legendWidth, legendHeight);
          // Draw border
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.strokeRect(legendX, legendY, legendWidth, legendHeight);
        } else {
          // Draw rounded rectangle
          ctx.beginPath();
          ctx.moveTo(legendX + radius, legendY);
          ctx.lineTo(legendX + legendWidth - radius, legendY);
          ctx.quadraticCurveTo(legendX + legendWidth, legendY, legendX + legendWidth, legendY + radius);
          ctx.lineTo(legendX + legendWidth, legendY + legendHeight - radius);
          ctx.quadraticCurveTo(legendX + legendWidth, legendY + legendHeight, legendX + legendWidth - radius, legendY + legendHeight);
          ctx.lineTo(legendX + radius, legendY + legendHeight);
          ctx.quadraticCurveTo(legendX, legendY + legendHeight, legendX, legendY + legendHeight - radius);
          ctx.lineTo(legendX, legendY + radius);
          ctx.quadraticCurveTo(legendX, legendY, legendX + radius, legendY);
          ctx.closePath();
          ctx.fill();
        }
        
        // Text
        ctx.fillStyle = colors[volunteerId]?.text || config.textColor;
        ctx.fillText(legendText, legendX + legendWidth / 2, legendY + legendHeight / 2);
        
        legendX += legendWidth + 6;
      });
      // Convert to image and save (native: Filesystem+Share; web: <a download>)
      const image = canvas.toDataURL('image/png');
      const dateString = dateEnabled ? timeRange.date.replace(/-/g, '') : 'nodate';
      const locationText = locationName ? `${locationName.replace(/\s+/g, '_')}` : 'Volunteer';
      const filename = `${locationText}_Schedule_${dateString}.png`;

      await saveImage({ dataUrl: image, filename });
      
      // Reset button state
      if (downloadBtn) {
        downloadBtn.innerHTML = originalContent;
      }
    } catch (err) {
      console.error('Error generating image:', err);
      alert(t('alerts.imageError'));
      if (downloadBtn) {
        downloadBtn.innerHTML = originalContent;
      }
    }
  };
  
  // The compact screenshot view component optimized for mobile
  const ScreenshotView = () => {
    if (!schedule.length) return null;
    
    const dateStr = dateEnabled ? formatDate(timeRange.date) : "";
    const timeStr = `${formatShiftTime(timeRange.startTime)}-${formatShiftTime(timeRange.endTime)}`;
    const shiftCounts = schedule[0]?.shiftCounts || {};
    const displayTitle = locationName ? t('screenshot.locationTitle', { location: locationName }) : t('screenshot.defaultTitle');
    
    return (
      <div className={`fixed inset-0 safe-top ${darkMode ? 'bg-gray-900' : 'bg-white'} z-50 overflow-auto ${audioMode ? 'eight-bit-container' : ''}`}>
        <div className="relative max-w-md mx-auto">
          {/* Controls - with better spacing for mobile */}
          <div className="absolute top-2 right-2 flex space-x-2">
            <button 
              onClick={toggleDarkMode}
              className={`p-2 ${audioMode ? 'eight-bit-button' : 'rounded-full'} ${darkMode ? 'text-yellow-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
              aria-label={darkMode ? t('header.lightMode') : t('header.darkMode')}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button 
              onClick={downloadScheduleImage}
              className={`p-2 ${audioMode ? 'eight-bit-button' : 'rounded-full'} ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'} download-btn`}
              aria-label={t('screenshot.downloadLabel')}
            >
              <Download size={20} />
            </button>
            <button 
              onClick={() => setScreenshotMode(false)}
              className={`p-2 ${audioMode ? 'eight-bit-button' : 'rounded-full'} ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
              aria-label={t('screenshot.closeLabel')}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Increased top padding to prevent controls overlap */}
          <div className="pt-12 pb-2 px-3" ref={scheduleRef}>
            {/* Header */}
            <div className="text-center mb-2">
              <h1 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'} ${audioMode ? 'eight-bit-text' : ''}`}>{displayTitle}</h1>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} ${audioMode ? 'eight-bit-text' : ''}`}>
                {dateEnabled ? `${dateStr} • ${timeStr}` : timeStr}
              </p>
            </div>

            {/* Discussion Point */}
            {scripturalPoint && (
              <div style={{
                marginBottom: '10px',
                padding: '12px',
                paddingLeft: audioMode ? '12px' : '16px',
                background: darkMode ? '#2d3748' : '#f0f4ff',
                border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                borderLeft: audioMode ? undefined : (darkMode ? '4px solid #818cf8' : '4px solid #4f46e5'),
                borderRadius: audioMode ? '0' : '6px',
                ...(audioMode ? { borderWidth: '2px' } : {})
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: darkMode ? '#a78bfa' : '#4f46e5',
                  marginBottom: '6px',
                  fontFamily: audioMode ? '"Press Start 2P", cursive' : 'inherit'
                }}>
                  {smpwMode ? t('schedulePanel.discussionScriptural') : t('schedulePanel.discussionPoint')}
                </div>
                <div style={{
                  fontSize: '15px',
                  lineHeight: '1.5',
                  color: darkMode ? '#e2e8f0' : '#1a202c',
                  whiteSpace: 'pre-wrap',
                  fontFamily: audioMode ? '"Press Start 2P", cursive' : 'inherit'
                }}>
                  {scripturalPoint}
                </div>
              </div>
            )}

            {/* Compact Schedule Table - Using pure HTML table for better rendering */}
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              borderSpacing: 0,
              fontSize: '12px',
              marginBottom: '8px',
              border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
              fontFamily: audioMode ? '"Press Start 2P", cursive' : 'inherit',
              ...(audioMode ? { imageRendering: 'pixelated', borderWidth: '2px' } : {})
            }}><thead>
            <tr style={{ 
              background: darkMode ? '#2d3748' : '#f7fafc'
            }}>
              <th style={{ 
                border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                padding: '4px 4px',
                textAlign: 'left',
                fontWeight: '500',
                color: darkMode ? '#e2e8f0' : '#1a202c',
                ...(audioMode ? { borderWidth: '2px', fontSize: '10px' } : {})
              }}>
                {t('schedulePanel.timeHeader')}
              </th>
              
              {multipleLocations ? (
                // Multiple locations table headers
                locationNames.map((name, index) => (
                  <th key={index} style={{ 
                    border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                    padding: '4px 4px',
                    textAlign: 'center',
                    fontWeight: '500',
                    color: darkMode ? '#e2e8f0' : '#1a202c',
                    ...(audioMode ? { borderWidth: '2px', fontSize: '10px' } : {})
                  }}>
                    {/* Truncate long location names in 8-bit mode */}
                    {audioMode && name.length > 8 ? `${name.substring(0, 7)}…` : name}
                  </th>
                ))
              ) : (
                // Single location header
                <th style={{ 
                  border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                  padding: '4px 4px',
                  textAlign: 'center',
                  fontWeight: '500',
                  color: darkMode ? '#e2e8f0' : '#1a202c',
                  ...(audioMode ? { borderWidth: '2px', fontSize: '10px' } : {})
                }}>
                  {t('schedulePanel.volunteersHeader')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {schedule.map((slot, index) => (
              <tr key={index} style={{ 
                background: index % 2 === 0 
                  ? (darkMode ? '#1a202c' : '#ffffff') 
                  : (darkMode ? '#2d3748' : '#f7fafc')
              }}>
                <td style={{ 
                  border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                  padding: '4px 4px',
                  whiteSpace: 'nowrap',
                  fontWeight: '500',
                  color: darkMode ? '#e2e8f0' : '#1a202c',
                  ...(audioMode ? { borderWidth: '2px', fontSize: audioMode ? '7px' : '10px' } : {})
                }}>
                  {/* Use smaller font size in 8-bit mode if needed */}
                  {slot.compactDisplay}
                </td>
                
                {multipleLocations ? (
                  // Multiple locations cells
                  slot.locations.map((location, locIndex) => (
                    <td key={locIndex} style={{ 
                      border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                      padding: '4px 2px',
                      textAlign: 'center',
                      ...(audioMode ? { borderWidth: '2px' } : {})
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                        {location.volunteers.map((volunteerId, vIndex) => {
                          // Get the volunteer name
                          const displayName = volunteerMap[volunteerId] || volunteerId;
                          // For 8-bit mode, truncate long names
                          const truncatedName = audioMode && displayName.length > 6 
                            ? `${displayName.substring(0, 5)}…` 
                            : displayName;
                          
                          return (
                            <div 
                              key={vIndex}
                              style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                minWidth: 0,
                                height: '22px',
                                backgroundColor: colors[volunteerId]?.bg || 'transparent',
                                color: colors[volunteerId]?.text || 'inherit',
                                borderRadius: audioMode ? '0px' : '2px',
                                fontSize: audioMode ? '8px' : '11px',
                                margin: '0 2px',
                                ...(audioMode ? { border: '2px solid #000' } : {})
                              }}
                            >
                              {truncatedName}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  ))
                ) : (
                  // Single location cell
                  <td style={{ 
                    border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                    padding: '4px 2px',
                    textAlign: 'center',
                    ...(audioMode ? { borderWidth: '2px' } : {})
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                      {slot.volunteers.map((volunteerId, vIndex) => {
                        // Get the volunteer name
                        const displayName = volunteerMap[volunteerId] || volunteerId;
                        // For 8-bit mode, truncate long names
                        const truncatedName = audioMode && displayName.length > 6 
                          ? `${displayName.substring(0, 5)}…` 
                          : displayName;
                        
                        return (
                          <div 
                            key={vIndex}
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '45%',
                              height: '22px',
                              backgroundColor: colors[volunteerId]?.bg || 'transparent',
                              color: colors[volunteerId]?.text || 'inherit',
                              borderRadius: audioMode ? '0px' : '2px',
                              fontSize: audioMode ? '8px' : '11px',
                              margin: '0 2px',
                              ...(audioMode ? { border: '2px solid #000' } : {})
                            }}
                          >
                            {truncatedName}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Legend using flex for vertical alignment */}
        <div style={{ fontSize: audioMode ? '8px' : '11px', marginBottom: '4px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {Object.keys(shiftCounts).map((volunteerId, index) => {
              const displayName = volunteerMap[volunteerId] || volunteerId;
              // For 8-bit mode, truncate long names
              const truncatedName = audioMode && displayName.length > 6 
                ? `${displayName.substring(0, 5)}…` 
                : displayName;
              
              return (
                <div 
                  key={index}
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '18px',
                    backgroundColor: colors[volunteerId]?.bg || 'transparent',
                    color: colors[volunteerId]?.text || 'inherit',
                    borderRadius: audioMode ? '0px' : '2px',
                    fontSize: audioMode ? '8px' : '11px',
                    padding: '0 4px',
                    margin: '0 2px',
                    ...(audioMode ? { border: '2px solid #000' } : {})
                  }}
                >
                  {truncatedName}:{shiftCounts[volunteerId]}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Scriptural Point (only in SMPW mode) */}
        {smpwMode && scripturalPoint && (
          <div className={`mt-4 p-3 rounded-md ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'} ${audioMode ? 'eight-bit-box' : ''}`}>
            <div className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'} ${audioMode ? 'eight-bit-text' : ''}`}>
              {t('schedulePanel.discussionScriptural')}
            </div>
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} ${audioMode ? 'eight-bit-text' : ''}`}>
              {scripturalPoint}
            </div>
          </div>
        )}
      </div>
      
      {/* Share button with status indication */}
      <div className="flex justify-center mt-4 mb-2">
        <button
          onClick={copyScheduleToClipboard}
          disabled={copyStatus === 'copying'}
          className={`flex items-center justify-center py-2 px-5 ${audioMode ? 'eight-bit-button' : 'rounded-lg'} font-medium copy-btn ${
            copyStatus === 'success' || copyStatus === 'copied'
              ? (darkMode ? 'bg-green-800 text-green-200' : 'bg-green-600 text-white')
              : copyStatus === 'error'
              ? (darkMode ? 'bg-red-800 text-red-200' : 'bg-red-600 text-white')
              : (darkMode ? 'bg-indigo-800 text-white' : 'bg-indigo-600 text-white')
          } transition-colors`}
        >
          {copyStatus === 'copying' ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('screenshot.share.processing')}
            </>
          ) : copyStatus === 'success' ? (
            <>
              <Check className="mr-2" size={20} />
              {t('screenshot.share.shared')}
            </>
          ) : copyStatus === 'copied' ? (
            <>
              <Check className="mr-2" size={20} />
              {t('screenshot.share.copied')}
            </>
          ) : copyStatus === 'error' ? (
            <>
              <AlertCircle className="mr-2" size={20} />
              {t('screenshot.share.tryAnother')}
            </>
          ) : (
            <>
              <Copy className="mr-2" size={20} />
              {t('screenshot.share.cta')}
            </>
          )}
        </button>
      </div>
      
      <div className="text-center mb-4">
        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${audioMode ? 'eight-bit-text' : ''}`}>
          {copyStatus === 'copied'
            ? t('screenshot.footer.copiedHint')
            : t('screenshot.footer.defaultHint')}
        </p>
      </div>
    </div>
    
  </div>
);
};

const maxSingleLocCount = schedule.length === 0
  ? volunteersPerShift
  : Math.max(...schedule.map(s => s.volunteerCount ?? volunteersPerShift));

const maxLocCount = schedule.length === 0
  ? locationNames.map(() => volunteersPerShift)
  : locationNames.map((_, locIndex) =>
      Math.max(
        ...schedule.map(s =>
          (s.locations?.[locIndex]?.volunteerCount) ?? volunteersPerShift
        )
      )
    );

return (
<div className={`min-h-screen flex flex-col ${darkMode ? 'bg-surface-950 text-white' : 'bg-surface-50'} ${audioMode ? 'eight-bit-container' : 'font-body'}`}>
  {/* Audio element for the Easter egg */}
  <audio ref={audioRef} src={`${process.env.PUBLIC_URL}/peopleofallsorts.mp3`} loop={audioMode && !audioMuted} />
  
  {/* SMPW Mode Activation Animation */}
  {showActivation && (
    <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className={`px-8 py-5 shadow-elevated transform animate-bounce-in ${audioMode ? 'eight-bit-box bg-white text-black' : 'rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600'}`}>
        <h2 className={`text-xl md:text-2xl font-bold ${audioMode ? 'eight-bit-text text-black' : 'text-white font-display'}`}>{activationMessage}</h2>
      </div>
    </div>
  )}
  
  {/* Mute button (appears only when audio is playing) */}
  {audioMode && (
    <button 
      onClick={() => {
        setAudioMuted(!audioMuted);
        if (audioRef.current) {
          if (audioMuted) {
            audioRef.current.play().catch(e => console.error('Audio playback failed:', e));
          } else {
            audioRef.current.pause();
          }
        }
      }}
      className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 p-3 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} ${audioMode ? 'eight-bit-button' : 'rounded-full'} shadow-lg z-50`}
      aria-label={audioMuted ? t('header.unmuteAudio') : t('header.muteAudio')}
    >
      {audioMuted ? <VolumeX size={20} className={darkMode ? 'text-white' : 'text-gray-800'} /> : <Volume2 size={20} className={darkMode ? 'text-white' : 'text-gray-800'} />}
    </button>
  )}
  
  {/* Header */}
  <header className={`sticky top-0 z-30 ${audioMode ? 'eight-bit-header safe-top' : ''}`}>
    <div className={audioMode ? '' : `relative safe-top border-b ${darkMode ? 'bg-surface-950/80 border-gray-800/50 backdrop-blur-xl supports-[backdrop-filter]:bg-surface-950/70' : 'bg-white/80 border-gray-200/60 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70'}`}>
      {!audioMode && <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#2D6AA0]/20 to-transparent" />}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-2 overflow-hidden">
          <h1
            className={`text-lg sm:text-xl font-bold flex items-center cursor-pointer select-none active:scale-95 transition-transform min-w-0 flex-1 ${audioMode ? 'eight-bit-heading text-white' : `font-display ${darkMode ? 'text-white' : 'text-gray-900'}`}`}
            onClick={handleTitleClick}
            tabIndex={0}
            role="button"
          >
            <div className={`mr-2.5 p-1.5 rounded-xl ${audioMode ? '' : `${darkMode ? 'bg-brand-500/10' : 'bg-[#2D6AA0]/10'}`}`}>
              <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 ${audioMode ? '' : `${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'}`}`} />
            </div>
            {smpwMode ? (
              <span>{audioMode ? "8-BIT SMPW" : "SMPW Scheduler"}</span>
            ) : audioMode ? (
              <span>8-BIT SCHEDULER</span>
            ) : (
              <span className={`flex items-baseline ${titleCollapsed ? 'title-collapsed' : ''} ${showSubtitle ? 'show-subtitle' : ''}`}>
                <span className="title-word"><span className="title-first-letter">S</span><span className="title-collapse">pecial</span></span>
                <span className="title-word-space" />
                <span className="title-word"><span className="title-first-letter">M</span><span className="title-collapse">etropolitan</span></span>
                <span className="title-word-space" />
                <span className="title-word"><span className="title-first-letter">P</span><span className="title-collapse">ublic</span></span>
                <span className="title-word-space" />
                <span className="title-word"><span className="title-first-letter">W</span><span className="title-collapse">itnessing</span></span>
                <span className="subtitle-wrap">
                  <span className="subtitle-text text-gray-400">{t('header.subtitle')}</span>
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                  <span className="splash-particle" />
                </span>
              </span>
            )}
          </h1>
          <div className="flex items-center gap-1 shrink-0">
          {i18nEnabled && (
            <React.Suspense fallback={null}>
              <LanguageSwitcher
                darkMode={darkMode}
                audioMode={audioMode}
                visible={showSubtitle || smpwMode || audioMode}
              />
            </React.Suspense>
          )}
          <button
            onClick={toggleDarkMode}
            className={`p-2.5 rounded-xl shrink-0 transition-all duration-[400ms] ${showSubtitle || smpwMode || audioMode ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${audioMode ? 'eight-bit-button !p-1.5 !border-2' : `${darkMode ? 'text-gray-400 hover:text-amber-300 hover:bg-gray-800/80' : 'text-gray-400 hover:text-[#2D6AA0] hover:bg-[#2D6AA0]/5'}`}`}
            aria-label={darkMode ? t('header.lightMode') : t('header.darkMode')}
          >
            <div className="transition-transform duration-300 hover:rotate-12">
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </div>
          </button>
          </div>
        </div>
      </div>
    </div>
  </header>

  <AppNudge darkMode={darkMode} />

  {screenshotMode && <ScreenshotView />}
  
  {/* Main content with mobile improvements */}
  <main className="py-4 px-2 sm:py-6 sm:px-4 max-w-6xl mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      {/* Setup Panel - Improved for mobile */}
      <div className={`${darkMode ? 'bg-surface-800 border border-gray-800' : 'bg-white border border-gray-100 shadow-card'} p-4 sm:p-6 ${audioMode ? 'eight-bit-panel' : 'rounded-2xl'}`}>
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 flex items-center ${audioMode ? 'eight-bit-heading' : 'font-display'}`}>
          <Users className={`mr-2 ${audioMode ? '' : `${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'}`}`} size={20} />
          {t('setup.title')}
        </h2>

        <div className="space-y-6">
          {/* Event Details Section */}
          <div className={`${darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'} ${audioMode ? 'eight-bit-panel' : 'rounded-lg'} p-4 border space-y-4`}>
            <h3 className={`text-sm font-semibold ${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'} uppercase tracking-wide mb-3 ${audioMode ? 'eight-bit-text' : ''}`}>
              <Building className="inline-block mr-1 mb-0.5" size={14} />
              {t('setup.sections.shiftDetails')}
            </h3>

          {/* Location Name */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
              {t('setup.location.label')}
            </label>
            {smpwMode ? (
              <select
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
              >
                <option value="">{t('setup.location.smpwPlaceholder')}</option>
                {smpwLocations.map((location, index) => (
                  <option key={index} value={location}>{location}</option>
                ))}
              </select>
            ) : (
              <LocationInput
                value={locationName}
                onChange={setLocationName}
                recents={recentLocations}
                onRemoveRecent={removeRecentLocation}
                darkMode={darkMode}
                audioMode={audioMode}
                placeholder={t('setup.location.namePlaceholder')}
              />
            )}
          </div>

          {/* Discussion Point - Available in all modes */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
              {smpwMode ? t('setup.discussion.labelSmpw') : t('setup.discussion.label')}
            </label>
            <textarea
              value={scripturalPoint}
              onChange={(e) => setScripturalPoint(e.target.value)}
              placeholder={smpwMode ? t('setup.discussion.placeholderSmpw') : t('setup.discussion.placeholder')}
              rows="3"
              className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
            />
          </div>

          {/* Multiple Locations Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="multipleLocations"
              checked={multipleLocations}
              onChange={handleMultipleLocationsChange}
              className={`h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 ${audioMode ? 'eight-bit-checkbox' : 'rounded'}`}
            />
            <label
              htmlFor="multipleLocations"
              className={`ml-2 block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${audioMode ? 'eight-bit-text' : ''}`}
            >
              {smpwMode ? t('setup.location.eastern') : t('setup.location.multiple')}
            </label>
            <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${audioMode ? 'eight-bit-text' : ''}`}>
              {smpwMode ? t('setup.location.easternHint') : t('setup.location.multipleHint')}
            </span>
          </div>
          
          {/* Location Names when Multiple Locations is checked */}
          {multipleLocations && !smpwMode && (
            <div className="space-y-3 pl-6 border-l-2 border-indigo-200 dark:border-indigo-800">
              {locationNames.map((name, index) => (
                <div key={index}>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                    {t('setup.location.nameN', { n: index + 1 })}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleLocationNameChange(index, e.target.value)}
                    placeholder={t('setup.location.placeholderN', { n: index + 1 })}
                    className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Date Toggle and Field */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${audioMode ? 'eight-bit-text' : ''}`}>
                {t('setup.schedule.includeDate')}
              </label>
              <button
                onClick={toggleDateEnabled}
                className={`flex items-center text-sm ${darkMode ? 'text-indigo-300' : 'text-indigo-600'} ${audioMode ? 'eight-bit-text' : ''}`}
              >
                {dateEnabled ? (
                  <>
                    <ToggleRight size={20} className="mr-1" />
                    <span>{t('setup.schedule.on')}</span>
                  </>
                ) : (
                  <>
                    <ToggleLeft size={20} className="mr-1" />
                    <span>{t('setup.schedule.off')}</span>
                  </>
                )}
              </button>
            </div>
            
            {dateEnabled && (
              <div className="relative">
                <input
                  type="date"
                  value={timeRange.date}
                  onChange={(e) => handleTimeChange('date', e.target.value)}
                  className={`w-full p-3 pr-10 border text-left ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base date-input-custom`}
                />
                {!audioMode && (
                  <Calendar
                    size={18}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  />
                )}
              </div>
            )}
          </div>
          </div>
          {/* End Event Details Section */}

          {/* Schedule Settings Section */}
          <div className={`${darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'} ${audioMode ? 'eight-bit-panel' : 'rounded-lg'} p-4 border space-y-4`}>
            <h3 className={`text-sm font-semibold ${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'} uppercase tracking-wide mb-3 ${audioMode ? 'eight-bit-text' : ''}`}>
              <Clock className="inline-block mr-1 mb-0.5" size={14} />
              {t('setup.sections.scheduleSettings')}
            </h3>

          {/* Time Range - Improved for mobile */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
              {t('setup.schedule.shiftTime')}
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
              <div className="w-full min-w-0">
                <label className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                  {t('setup.schedule.startTime')}
                </label>
                <input
                  type="time"
                  value={timeRange.startTime}
                  onChange={(e) => handleTimeChange('startTime', e.target.value)}
                  className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base box-border time-input-custom`}
                  style={{ minWidth: 0 }}
                />
              </div>
              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-center sm:mx-2 ${audioMode ? 'eight-bit-text' : ''} flex-shrink-0`}>{t('setup.schedule.to')}</span>
              <div className="w-full min-w-0">
                <label className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                  {t('setup.schedule.endTime')}
                </label>
                <input
                  type="time"
                  value={timeRange.endTime}
                  onChange={(e) => handleTimeChange('endTime', e.target.value)}
                  className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base box-border time-input-custom`}
                  style={{ minWidth: 0 }}
                />
              </div>
            </div>
          </div>
          
          {/* Shift Interval */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
              {t('setup.schedule.durationQuestion')}
            </label>
            <div className="relative">
              <select
                value={timeRange.isCustomInterval ? 'custom' : timeRange.interval}
                onChange={(e) => handleTimeChange('interval', e.target.value)}
                className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded appearance-none'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
              >
                <option value="20">{t('setup.schedule.minutes', { n: 20 })}</option>
                <option value="25">{t('setup.schedule.minutes', { n: 25 })}</option>
                <option value="30">{t('setup.schedule.minutes', { n: 30 })}</option>
                <option value="custom">{t('setup.schedule.custom')}</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
              </div>
            </div>

            {/* Custom interval input */}
            {timeRange.isCustomInterval && (
              <div className="mt-2">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                  {t('setup.schedule.customMinutes')}
                </label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={timeRange.customInterval}
                  onChange={(e) => handleTimeChange('customInterval', e.target.value)}
                  className={`w-full p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
                />
              </div>
            )}
          </div>

          {/* Advanced options disclosure */}
          <div className={`pt-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={() => setAdvancedExpanded(!advancedExpanded)}
              className={`flex items-center w-full text-sm font-medium ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-700 hover:text-gray-900'} ${audioMode ? 'eight-bit-text' : ''}`}
              aria-expanded={advancedExpanded}
              aria-controls="advanced-options-panel"
            >
              <ChevronDown
                size={16}
                className={`mr-1 transition-transform ${advancedExpanded ? 'rotate-0' : '-rotate-90'}`}
              />
              {t('setup.schedule.advancedOptions')}
            </button>

            {advancedExpanded && (
              <div
                id="advanced-options-panel"
                className={`mt-3 p-3 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border ${audioMode ? 'eight-bit-box' : 'rounded'} space-y-4`}
              >

                {/* Volunteers per shift stepper */}
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
                    {t('setup.schedule.volunteersPerShift')}
                  </label>
                  {(() => {
                    const canIncrement = volunteersPerShift < VOLUNTEERS_MAX;
                    const canDecrement = volunteersPerShift > VOLUNTEERS_MIN;
                    return (
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => setVolunteersPerShift(volunteersPerShift - 1)}
                          disabled={!canDecrement}
                          className={`px-3 py-2 ${audioMode ? 'eight-bit-button' : 'rounded'} ${
                            canDecrement
                              ? (darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-900 hover:bg-gray-300')
                              : (darkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400')
                          }`}
                          aria-label="Decrease volunteers per shift"
                        >
                          −
                        </button>
                        <div
                          aria-live="polite"
                          aria-atomic="true"
                          className={`px-4 py-2 min-w-[3rem] text-center font-medium ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} ${audioMode ? 'eight-bit-box' : 'rounded border ' + (darkMode ? 'border-gray-600' : 'border-gray-300')}`}
                        >
                          {volunteersPerShift}
                        </div>
                        <button
                          type="button"
                          onClick={() => setVolunteersPerShift(volunteersPerShift + 1)}
                          disabled={!canIncrement}
                          className={`px-3 py-2 ${audioMode ? 'eight-bit-button' : 'rounded'} ${
                            canIncrement
                              ? (darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-900 hover:bg-gray-300')
                              : (darkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400')
                          }`}
                          aria-label="Increase volunteers per shift"
                        >
                          +
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Allow back-to-back toggle */}
                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="allowBackToBackToggle"
                      className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${audioMode ? 'eight-bit-text' : ''}`}
                    >
                      {t('setup.schedule.allowBackToBack')}
                    </label>
                    <button
                      id="allowBackToBackToggle"
                      type="button"
                      role="switch"
                      aria-checked={allowBackToBack}
                      onClick={() => setAllowBackToBack(!allowBackToBack)}
                      className={`relative inline-flex h-6 w-11 items-center ${audioMode ? 'eight-bit-button' : 'rounded-full'} transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        allowBackToBack
                          ? 'bg-indigo-600'
                          : (darkMode ? 'bg-gray-600' : 'bg-gray-300')
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform bg-white ${audioMode ? '' : 'rounded-full'} transition-transform ${
                          allowBackToBack ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${audioMode ? 'eight-bit-text' : ''}`}>
                    {t('setup.schedule.allowBackToBackHint')}
                  </p>
                </div>

                {/* Reset to defaults link — only when non-default */}
                {!isAtDefaults({ volunteersPerShift, allowBackToBack }) && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={resetAdvancedSettings}
                      className={`text-xs ${darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'} ${audioMode ? 'eight-bit-text' : 'underline'}`}
                    >
                      {t('setup.schedule.resetDefaults')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          </div>
          {/* End Schedule Settings Section */}

          {/* Volunteers Section */}
          <div className={`${darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'} ${audioMode ? 'eight-bit-panel' : 'rounded-lg'} p-4 border space-y-4`}>
            <h3 className={`text-sm font-semibold ${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'} uppercase tracking-wide mb-3 ${audioMode ? 'eight-bit-text' : ''}`}>
              <Users className="inline-block mr-1 mb-0.5" size={14} />
              {t('setup.sections.volunteers')}
            </h3>

          {/* Volunteers - Improved spacing for mobile with delete buttons */}
          <div>
            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
              {t('setup.volunteers.label')} {multipleLocations && <span className="text-xs font-normal ml-1">
                {t('setup.volunteers.minHint', { n: volunteersPerShift * locationNames.length })}
              </span>}
            </label>
            <div className="space-y-2">
              {volunteers.map((volunteer, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={volunteer}
                    onChange={(e) => handleVolunteerChange(index, e.target.value)}
                    placeholder={t('setup.volunteers.placeholder', { n: index + 1 })}
                    className={`flex-1 p-3 border ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} ${audioMode ? 'eight-bit-button' : 'rounded'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base`}
                  />
                  <button
                    onClick={() => deleteVolunteer(index)}
                    disabled={volunteers.length <= 2} // Prevent deletion if only 2 volunteers
                    className={`p-3 ${audioMode ? 'eight-bit-button' : 'rounded'} ${
                      volunteers.length <= 2
                        ? (darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400')
                        : (darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-100 text-red-500 hover:bg-red-200')
                    }`}
                    aria-label={t('setup.volunteers.delete')}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addVolunteer}
              className={`mt-3 inline-flex items-center px-4 py-3 text-sm font-medium ${
                darkMode
                  ? 'text-indigo-300 bg-indigo-900 hover:bg-indigo-800'
                  : 'text-indigo-600 bg-indigo-100 hover:bg-indigo-200'
              } ${audioMode ? 'eight-bit-button' : 'rounded'}`}
            >
              <Plus size={18} className="mr-1" />
              {t('setup.volunteers.add')}
            </button>
          </div>
          </div>
          {/* End Volunteers Section */}

          {/* Generate Button */}
          <button
            onClick={generateSchedule}
            disabled={isLoading}
            className={`generate-btn font-display flex items-center justify-center ${audioMode ? 'eight-bit-button !text-base' : ''}`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('setup.generate.loading')}
              </>
            ) : t('setup.generate.cta')}
          </button>
        </div>
      </div>
      
      {/* Schedule Panel - Improved for mobile */}
      <div className={`${darkMode ? 'bg-surface-800 border border-gray-800' : 'bg-white border border-gray-100 shadow-card'} p-4 sm:p-6 ${audioMode ? 'eight-bit-panel' : 'rounded-2xl'}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center ${audioMode ? 'eight-bit-heading' : 'font-display'}`}>
            <Calendar className={`mr-2 ${audioMode ? '' : `${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'}`}`} size={20} />
            {t('schedulePanel.title')}
          </h2>
          
          {schedule.length > 0 && (
            <button
              onClick={shuffleSchedule}
              disabled={isLoading}
              className={`inline-flex items-center px-3 py-1 text-sm font-medium ${
                darkMode 
                  ? 'text-purple-300 bg-purple-900 hover:bg-purple-800' 
                  : 'text-purple-700 bg-purple-100 hover:bg-purple-200'
              } ${audioMode ? 'eight-bit-button' : 'rounded'} mr-2`}
            >
              <Shuffle size={16} className="mr-1" />
              {t('schedulePanel.shuffle')}
            </button>
          )}
        </div>

        {schedule.length > 0 ? (
          <>
            {/* Discussion Point Display */}
            {scripturalPoint && (
              <div className={`mb-4 p-3 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border ${audioMode ? 'eight-bit-box' : 'rounded-md'}`}>
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-brand-400' : 'text-[#2D6AA0]'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>
                  <Info className="inline-block mr-1 mb-0.5" size={14} />
                  {smpwMode ? t('schedulePanel.discussionScriptural') : t('schedulePanel.discussionPoint')}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap ${audioMode ? 'eight-bit-text' : ''}`}>
                  {scripturalPoint}
                </p>
              </div>
            )}

            {conflicts.length > 0 && (
              <div className={`mb-4 p-3 ${darkMode ? 'bg-yellow-900 border-yellow-800 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'} border ${audioMode ? 'eight-bit-box' : 'rounded-md'} text-sm flex items-start`}>
                <AlertTriangle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className={`font-medium ${audioMode ? 'eight-bit-text' : ''}`}>{t('schedulePanel.warningTitle')}</p>
                  <p className={audioMode ? 'eight-bit-text' : ''}>{t('schedulePanel.warningBody')}</p>
                </div>
              </div>
            )}
            
            {/* Volunteer Summary */}
            {schedule.length > 0 && (
              <div className={`mb-4 p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} ${audioMode ? 'eight-bit-box' : 'rounded-md'}`}>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-2 ${audioMode ? 'eight-bit-text' : ''}`}>{t('schedulePanel.volunteerShifts')}</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(schedule[0].shiftCounts || {}).map(([volunteerId, count], index) => (
                    <div 
                      key={index}
                      className={`px-2 py-1 ${audioMode ? 'eight-bit-box' : 'rounded'} text-sm inline-flex items-center justify-center`}
                      style={{ 
                        backgroundColor: colors[volunteerId]?.bg || 'transparent',
                        color: colors[volunteerId]?.text || 'inherit',
                        ...(audioMode ? { border: '2px solid #000' } : {})
                      }}
                    >
                      {volunteerMap[volunteerId] || volunteerId}: {count}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Schedule Table - Different display for multiple locations */}
            {multipleLocations ? (
              // Multiple locations schedule table
              <div className={`overflow-x-auto overflow-y-auto max-h-80 sm:max-h-96 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${audioMode ? 'eight-bit-box' : 'rounded-md'}`}>
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} sticky top-0`}>
                    <tr>
                      <th className={`px-2 sm:px-3 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider ${audioMode ? 'eight-bit-text' : ''}`}>
                        {t('schedulePanel.timeHeader')}
                      </th>
                      {locationNames.map((name, locIndex) => (
                        <th
                          key={locIndex}
                          colSpan={maxLocCount[locIndex]}
                          className={`px-2 sm:px-3 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider ${audioMode ? 'eight-bit-text' : ''}`}
                        >
                          {audioMode && name.length > 8 ? `${name.substring(0, 7)}…` : name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {schedule.map((slot, slotIndex) => (
                      <tr key={slotIndex} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <td className={`px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'} ${audioMode ? 'eight-bit-text' : ''}`}>
                          {slot.display12}
                        </td>
                        {slot.locations.map((location, locIndex) => {
                          const rowCount = location.volunteerCount ?? volunteersPerShift;
                          return (
                            <td key={locIndex} colSpan={maxLocCount[locIndex]} className="p-0">
                              <div className="flex items-stretch">
                                <div
                                  className="grid flex-1"
                                  style={{ gridTemplateColumns: `repeat(${rowCount}, minmax(0, 1fr))` }}
                                >
                                  {Array.from({ length: rowCount }, (_, volIndex) => {
                                    const volunteerId = location.volunteers[volIndex];
                                    const hasConflict = conflicts.some(c =>
                                      c.slotIndex === slotIndex &&
                                      c.locationIndex === locIndex &&
                                      c.volunteer === volunteerId
                                    );
                                    const hasDuplicateError = duplicateError &&
                                      duplicateError.slotIndex === slotIndex &&
                                      duplicateError.locationIndex === locIndex;

                                    return (
                                      <div key={volIndex} className="px-1 sm:px-2 py-2 whitespace-nowrap text-xs sm:text-sm">
                                        <div className={`${audioMode ? '' : 'rounded'} ${hasConflict ? (darkMode ? 'bg-red-900' : 'bg-red-50') : ''} ${hasDuplicateError ? (darkMode ? 'border border-red-700' : 'border border-red-300') : ''}`}>
                                          <select
                                            value={volunteerId || ''}
                                            onChange={(e) => updateScheduleVolunteer(slotIndex, volIndex, e.target.value, locIndex)}
                                            className={`w-full py-1 px-2 ${audioMode ? 'eight-bit-button' : 'rounded border-0'} focus:ring-0 text-xs sm:text-sm ${darkMode ? 'bg-gray-700 text-white' : ''}`}
                                            style={{
                                              backgroundColor: volunteerId ? colors[volunteerId]?.bg : (darkMode ? '#1F2937' : 'transparent'),
                                              color: volunteerId ? colors[volunteerId]?.text : 'inherit'
                                            }}
                                          >
                                            <option value="" className={darkMode ? 'bg-gray-700' : ''}>{t('schedulePanel.selectVolunteer')}</option>
                                            {Object.keys(volunteerMap).map((id) => (
                                              <option key={id} value={id} className={darkMode ? 'bg-gray-700' : ''}>
                                                {volunteerMap[id]}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        {hasConflict && (
                                          <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'} mt-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                                            {t('schedulePanel.backToBack')}
                                          </div>
                                        )}
                                        {hasDuplicateError && volIndex === 0 && (
                                          <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'} mt-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                                            {duplicateError.message}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="flex flex-col justify-center px-1 gap-0.5">
                                  {rowCount < volunteersPerShift + EXTRA_VOLUNTEERS_MAX && (
                                    <button
                                      type="button"
                                      onClick={() => adjustLocationVolunteerCount(slotIndex, locIndex, 1)}
                                      aria-label={t('schedulePanel.addVolunteerSlot')}
                                      className={`w-5 h-5 leading-none rounded text-xs ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                    >
                                      +
                                    </button>
                                  )}
                                  {rowCount > volunteersPerShift && (
                                    <button
                                      type="button"
                                      onClick={() => adjustLocationVolunteerCount(slotIndex, locIndex, -1)}
                                      aria-label={t('schedulePanel.removeVolunteerSlot')}
                                      className={`w-5 h-5 leading-none rounded text-xs ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                    >
                                      −
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Original single location schedule table
              <div className={`overflow-x-auto overflow-y-auto max-h-80 sm:max-h-96 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${audioMode ? 'eight-bit-box' : 'rounded-md'}`}>
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} sticky top-0`}>
                    <tr>
                      <th className={`px-2 sm:px-3 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider ${audioMode ? 'eight-bit-text' : ''}`}>
                        {t('schedulePanel.timeHeader')}
                      </th>
                      <th
                        colSpan={maxSingleLocCount}
                        className={`px-2 sm:px-3 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider ${audioMode ? 'eight-bit-text' : ''}`}
                      >
                        {t('schedulePanel.volunteersHeader')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {schedule.map((slot, slotIndex) => {
                      const rowCount = slot.volunteerCount ?? volunteersPerShift;
                      return (
                        <tr key={slotIndex} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                          <td className={`px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'} ${audioMode ? 'eight-bit-text' : ''}`}>
                            {slot.display12}
                          </td>
                          <td colSpan={maxSingleLocCount} className="p-0">
                            <div className="flex items-stretch">
                              <div
                                className="grid flex-1"
                                style={{ gridTemplateColumns: `repeat(${rowCount}, minmax(0, 1fr))` }}
                              >
                                {Array.from({ length: rowCount }, (_, volIndex) => {
                                  const volunteerId = slot.volunteers[volIndex];
                                  const hasConflict = conflicts.some(c =>
                                    c.slotIndex === slotIndex && c.volunteer === volunteerId
                                  );
                                  const hasDuplicateError = duplicateError &&
                                    duplicateError.slotIndex === slotIndex;

                                  return (
                                    <div key={volIndex} className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm">
                                      <div className={`${audioMode ? '' : 'rounded'} ${hasConflict ? (darkMode ? 'bg-red-900' : 'bg-red-50') : ''} ${hasDuplicateError ? (darkMode ? 'border border-red-700' : 'border border-red-300') : ''}`}>
                                        <select
                                          value={volunteerId || ''}
                                          onChange={(e) => updateScheduleVolunteer(slotIndex, volIndex, e.target.value)}
                                          className={`w-full py-1 px-2 ${audioMode ? 'eight-bit-button' : 'rounded border-0'} focus:ring-0 text-xs sm:text-sm ${darkMode ? 'bg-gray-700 text-white' : ''}`}
                                          style={{
                                            backgroundColor: volunteerId ? colors[volunteerId]?.bg : (darkMode ? '#1F2937' : 'transparent'),
                                            color: volunteerId ? colors[volunteerId]?.text : 'inherit'
                                          }}
                                        >
                                          <option value="" className={darkMode ? 'bg-gray-700' : ''}>Select volunteer</option>
                                          {Object.keys(volunteerMap).map((id) => (
                                            <option key={id} value={id} className={darkMode ? 'bg-gray-700' : ''}>
                                              {volunteerMap[id]}
                                            </option>
                                          ))}
                                        </select>
                                      </div>

                                      {hasConflict && (
                                        <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'} mt-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                                          {t('schedulePanel.backToBack')}
                                        </div>
                                      )}

                                      {hasDuplicateError && volIndex === 0 && (
                                        <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'} mt-1 ${audioMode ? 'eight-bit-text' : ''}`}>
                                          {duplicateError.message}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex flex-col justify-center px-1 gap-0.5">
                                {rowCount < volunteersPerShift + EXTRA_VOLUNTEERS_MAX && (
                                  <button
                                    type="button"
                                    onClick={() => adjustSlotVolunteerCount(slotIndex, 1)}
                                    aria-label={t('schedulePanel.addVolunteerSlot')}
                                    className={`w-5 h-5 leading-none rounded text-xs ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                  >
                                    +
                                  </button>
                                )}
                                {rowCount > volunteersPerShift && (
                                  <button
                                    type="button"
                                    onClick={() => adjustSlotVolunteerCount(slotIndex, -1)}
                                    aria-label={t('schedulePanel.removeVolunteerSlot')}
                                    className={`w-5 h-5 leading-none rounded text-xs ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                                  >
                                    −
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Finished Button - Larger for better mobile touch target */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={enterScreenshotMode}
                className={`inline-flex items-center px-5 py-2 text-base font-medium text-white bg-green-600 ${audioMode ? 'eight-bit-button' : 'rounded'} hover:bg-green-700`}
              >
                <Camera size={18} className="mr-2" />
                {t('schedulePanel.finished')}
              </button>
            </div>
          </>
        ) : (
          <div className={`flex flex-col items-center justify-center h-52 md:h-64 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Calendar size={48} className="mb-4 opacity-30" />
            <p className={audioMode ? 'eight-bit-text' : ''}>{t('schedulePanel.empty.title')}</p>
            <p className={`text-sm ${audioMode ? 'eight-bit-text' : ''}`}>{t('schedulePanel.empty.hint')}</p>
          </div>
        )}
      </div>
    </div>
  </main>
  
  <footer className={`mt-auto pt-8 pb-12 border-t ${darkMode ? 'border-gray-800/50 text-gray-500' : 'border-gray-100 text-gray-400'} ${audioMode ? 'eight-bit-footer' : ''}`}>
  <div className="max-w-6xl mx-auto px-2 sm:px-4 flex flex-col items-center">
    {audioMode && (
      <div className="mb-8 mt-4">
        <img 
          src={`${process.env.PUBLIC_URL}/${
            showFellAnimation ? 'fell.gif' : 
            showLowHpAnimation ? 'lowhp.gif' : 
            showPokeAnimation ? 'poking_brandon.gif' : 
            'wave.gif'
          }`} 
          alt="8-bit animation" 
          className="h-64 sm:h-96 md:h-160 lg:h-200 w-auto max-w-full cursor-pointer"
          style={{ imageRendering: 'pixelated' }}
          onClick={handleWaveClick}
        />
      </div>
    )}
    {!isNative() && !audioMode && (
      <div className="mt-6 flex flex-col items-center gap-2">
        <div className={`text-[11px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('footer.alsoAvailable')}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StoreBadge platform="ios" size="compact" />
          <StoreBadge platform="android" size="compact" />
        </div>
      </div>
    )}
    <div className="flex flex-col sm:flex-row justify-between items-center text-center text-xs w-full mt-6">
      <div className={audioMode ? 'eight-bit-text' : ''}>v {appVersion} {audioMode && t('footer.audioMode')}</div>
      <div className={`mt-1 sm:mt-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'} ${audioMode ? 'eight-bit-text' : ''}`}>
        {t('footer.schedulesMade', { num: schedulesGenerated.toLocaleString() })}
      </div>
    </div>
  </div>
</footer>

<React.Suspense fallback={null}>
  <BackToBackConfirmDialog
    open={confirmDialog.open}
    count={confirmDialog.count}
    darkMode={darkMode}
    audioMode={audioMode}
    onCancel={() => setConfirmDialog({ open: false, count: 0 })}
    onConfirm={() => {
      setConfirmDialog({ open: false, count: 0 });
      proceedToScreenshotMode();
    }}
  />
</React.Suspense>

</div>
);
};

export default ScheduleBuilder;